import React from "react";
import { useTransition, animated, useSpring } from "react-spring";
import { schema, uiSchema } from "wn-claims-shared/dist/schema/geyser-schema";
import Wizard from "../../containers/Wizard";
import { objectMap } from "../../utils/common";
import formData from "./data";

var schemas = [];
var uiSchemas = [];

// Initialize schemas and uiSchemas
objectMap(schema.properties, (value) => {
  schemas.push(value);
  uiSchemas.push(uiSchema);
});

const checkCondition = (claimNonMotor, claimNonMotorCauseOfLoss, NonMotorCausedBy, conditions) => {
  return conditions.some(
    ([nonmotor, cause, validCauses]) =>
      claimNonMotor === nonmotor &&
      claimNonMotorCauseOfLoss === cause &&
      validCauses.includes(NonMotorCausedBy)
  );
};

const Geyser = ({ selectFormData, onBack }) => {
  const fadeIn = useSpring({ opacity: 1, from: { opacity: 0 } });
  const {
    claimtypeCat = "",
    claimNonMotor = "",
    claimNonMotorCauseOfLoss = "",
    NonMotorCausedBy = "",
    thirdparty = ""
  } = selectFormData;

  const NonmotorClaims = [
    "Accidental Damage",
    "Accounts Receivable",
    "All Risk",
    "Body Corporate",
    "Buildings Combined",
    "Business All Risks",
    "Business Interruption",
    "Contract Works",
    "Electronic Equipment",
    "Employers Liability",
    "Fidelity Guarantee",
    "Fire",
    "Glass",
    "Goods In Transit",
    "Group Personal Accident",
    "Homeowners",
    "House Holders",
    "Machinery Breakdown",
    "Money",
    "Office Contents",
    "Personal Liability",
    "Plant",
    "Public Liability",
    "Special Risks",
    "Stated Benefits",
    "Theft",
    "Watercraft"
  ];

  const removeSchemas = (index, count) => {
    schemas.splice(index, count); // Assuming `schemas` is defined elsewhere
  };

  const RemoveQuestions = (any) => {
    const relevantClaimTypes = [
      "Buildings Combined",
      "Fire",
      "House Holders",
      "Office Contents",
      "Homeowners"
    ];

    if (
      (claimNonMotorCauseOfLoss === "Geyser" && claimNonMotor === "Special Risks") ||
      claimNonMotor === "Special Risks"
    ) {
      schemas[0].required.splice(4, 1);
      schemas[0].required.splice(4, 1); // original index 5 is now at index 4
      schemas[0].required.splice(4, 1); // original index 6 is now at index 4

      // Safely remove the `Geyser field` property if it exists
      if (schemas[0]?.properties?.incidentgeysersize) {
        delete schemas[0].properties.incidentgeysersize;
      }
      if (schemas[0]?.properties?.incidentburstgeyser) {
        delete schemas[0].properties.incidentburstgeyser;
      }
      if (schemas[0]?.properties?.incidentDamages) {
        delete schemas[0].properties.incidentDamages;
      }
    }
    if (
      claimNonMotorCauseOfLoss !== "Geyser" &&
      !["Repair", "Replace", "With Resultant Water Damage"].includes(NonMotorCausedBy)
    ) {
      schemas[0].required.splice(4, 1);
      schemas[0].required.splice(4, 1); // original index 5 is now at index 4
      schemas[0].required.splice(4, 1); // original index 6 is now at index 4

      // Safely remove the `Geyser field` property if it exists
      if (schemas[0]?.properties?.incidentgeysersize) {
        delete schemas[0].properties.incidentgeysersize;
      }
      if (schemas[0]?.properties?.incidentburstgeyser) {
        delete schemas[0].properties.incidentburstgeyser;
      }
      if (schemas[0]?.properties?.incidentDamages) {
        delete schemas[0].properties.incidentDamages;
      }
    }
  };
  const removeSchemasByTitle = (titleToRemove) => {
    // Assuming `schemas` and `uiSchemas` are defined elsewhere
    schemas = schemas.filter((schema) => schema.title !== titleToRemove);
    uiSchemas = uiSchemas.filter((_, index) => schemas[index]?.title !== titleToRemove);
  };

  // Define conditions for different causes
  const conditions = [
    //Theft
    {
      causes: [
        //"Accidental Damage"
        [
          "Accidental Damage",
          "Accidental Damage",
          [
            "Accidental Damage General",
            "Contamination",
            "Aerial Fittings",
            "Impact by Aircraft / Aerial Devices",
            "Impact by Animals / Trees / Vehicles",
            "Lost / Misplaced",
            "Power Surge"
          ]
        ],
        [
          "Accidental Damage",
          "Leakage",
          [
            "Chemicals / Oils / Fumes",
            "Water - from Fire Extinguishing Installations",
            "Water - from Pipes"
          ]
        ],
        [
          "Accidental Damage",
          "Accidental Damage - Wine",
          [
            "Breakage - Bottled Wine",
            "Breakage - Bottles",
            "Breakage - Tanks",
            "Contamination",
            "Fermentation",
            "Latent Defect",
            "Leakage - Stock",
            "Leakage - Surrounding Property - Not Wine",
            "Leakage - Surrounding Property - Wine",
            "Loading / Off Loading"
          ]
        ],
        //Accounts Receivable
        ["Accounts Receivable", "Accidental Damage", ["Accidental Damage General"]],

        ["Accounts Receivable", "Explosion", ["Explosion"]],
        [
          "Accounts Receivable",
          "Fire",
          [
            "Electrical Fault",
            "Explosion",
            "External source",
            "Fire General",
            "Mechanical Fault",
            "Poor Housekeeping / Lack of Care",
            "Source off Premises",
            "Spontaneous Combustion",
            "Subterranean"
          ]
        ],
        ["Accounts Receivable", "Earthquake", ["Mining Operations", "Natural Phenomenon"]],
        [
          "Accounts Receivable",
          "Leakage",
          [
            "Chemicals / Oils / Fumes",
            "Water - from Pipes",
            "Water - from Fire Extinguishing Installations"
          ]
        ],
        [
          "Accounts Receivable",
          "Malicious Damage",
          ["Malicious Damage", "Malicious Damage With Theft"]
        ],
        ["Accounts Receivable", "Riot & Strike", ["NASRIA", "Outside RSA / NAM", "SASRIA"]],
        [
          "Accounts Receivable",
          "Storm",
          ["Hail / Snow", "Lightning", "Storm / Wind", "Water / Flood"]
        ],
        ["Accounts Receivable", "Subsidence & Landslip", ["Extended", "Limited"]],

        //all risk
        ["All Risk", "Accidental Damage", ["Accidental Damage General", "Lost / Misplaced"]],
        ["All Risk", "Earthquake", ["Mining Operations", "Natural Phenomenon"]],
        ["All Risk", "Explosion", ["Explosion"]],
        [
          "All Risk",
          "Fire",
          [
            "Electrical Fault",
            "Explosion",
            "External source",
            "Fire General",
            "Mechanical Fault",
            "Poor Housekeeping / Lack of Care",
            "Source off Premises",
            "Spontaneous Combustion",
            "Subterranean"
          ]
        ],
        [
          "All Risk",
          "Leakage",
          [
            "Chemicals / Oils / Fumes",
            "Water - from Fire Extinguishing Installations",
            "Water - from Pipes"
          ]
        ],
        ["All Risk", "Malicious Damage", ["Malicious Damage", "Malicious Damage With Theft"]],
        ["All Risk", "Riot & Strike", ["NASRIA", "Outside RSA / NAM", "SASRIA"]],
        ["All Risk", "Storm", ["Hail / Snow", "Lightning", "Storm / Wind", "Water / Flood"]],
        ["All Risk", "Subsidence & Landslip", ["Extended", "Limited"]],
        [
          "All Risk",
          "Theft",
          [
            "From Premises",
            "From Unattended Vehicle",
            "General",
            "Hijack",
            "Temporarily Removed from Building",
            "With Force",
            "Without Force"
          ]
        ],
        //body corporate
        [
          "Body Corporate",
          "Accidental Damage",
          [
            "Aerial Fittings",
            "Accidental Damage General",
            "Domestic Machinery",
            "Glass Buildings",
            "Glass Sanitaryware",
            "Impact by Aircraft / Aerial Devices",
            "Impact by Animals / Trees / Vehicles",
            "Power Surge",
            "Public Supply Connections",
            "Tree Removal Costs",
            "Wild Animals Damage"
          ]
        ],
        ["Body Corporate", "Death", ["Accidental", "Building / Premises"]],
        ["Body Corporate", "Earthquake", ["Mining Operations", "Natural Phenomenon"]],
        ["Body Corporate", "Explosion", ["Explosion"]],
        [
          "Body Corporate",
          "Fire",
          [
            "Electrical Fault",
            "Explosion",
            "External source",
            "Fire General",
            "Mechanical Fault",
            "Poor Housekeeping / Lack of Care",
            "Source off Premises",
            "Spontaneous Combustion",
            "Subterranean"
          ]
        ],
        [
          "Body Corporate",
          "Geyser",
          ["Repair", "Replacement", "With Resultant Water Damage", "Replace"]
        ],
        ["Body Corporate", "Illness", ["Accidental", "Building / Premises"]],
        ["Body Corporate", "Injury", ["Accidental", "Building / Premises"]],
        [
          "Body Corporate",
          "Leakage",
          [
            "Chemicals / Oils / Fumes",
            "Water - from Fire Extinguishing Installations",
            "Water - from Pipes",
            "Water - Leakage Costs"
          ]
        ],
        ["Body Corporate", "Liability", ["General", "Security Firm"]],
        ["Body Corporate", "Locks & Keys", ["Locks & Keys"]],
        ["Body Corporate", "Malicious Damage", ["Malicious Damage", "Malicious Damage With Theft"]],
        ["Body Corporate", "Riot & Strike", ["NASRIA", "Outside RSA / NAM", "SASRIA"]],
        ["Body Corporate", "Storm", ["Hail / Snow", "Lightning", "Storm / Wind", "Water / Flood"]],
        ["Body Corporate", "Subsidence & Landslip", ["Extended", "Limited"]],
        [
          "Body Corporate",
          "Theft",
          ["From Premises", "From Unattended Vehicle", "General", "With Force", "Without Force"]
        ],
        //Buildings Combined
        [
          "Buildings Combined",
          "Accidental Damage",
          [
            "Aerial Fittings",
            "Accidental Damage General",
            "Domestic Machinery",
            "Glass Buildings",
            "Glass Sanitaryware",
            "Impact by Aircraft / Aerial Devices",
            "Impact by Animals / Trees / Vehicles",
            "Power Surge",
            "Public Supply Connections",
            "Tree Removal Costs",
            "Wild Animals Damage"
          ]
        ],
        ["Buildings Combined", "Death", ["Accidental", "Building / Premises"]],
        ["Buildings Combined", "Earthquake", ["Mining Operations", "Natural Phenomenon"]],
        ["Buildings Combined", "Explosion", ["Explosion"]],
        [
          "Buildings Combined",
          "Fire",
          [
            "Electrical Fault",
            "Explosion",
            "External source",
            "Fire General",
            "Mechanical Fault",
            "Poor Housekeeping / Lack of Care",
            "Source off Premises",
            "Spontaneous Combustion",
            "Subterranean"
          ]
        ],
        ["Buildings Combined", "Geyser", ["Repair", "Replacement", "With Resultant Water Damage"]],
        ["Buildings Combined", "Illness", ["Accidental", "Building / Premises"]],
        ["Buildings Combined", "Injury", ["Accidental", "Building / Premises"]],
        [
          "Buildings Combined",
          "Leakage",
          [
            "Chemicals / Oils / Fumes",
            "Water - from Fire Extinguishing Installations",
            "Water - from Pipes",
            "Water - Leakage Costs"
          ]
        ],
        ["Buildings Combined", "Liability", ["General", "Security Firm"]],
        ["Buildings Combined", "Locks & Keys", ["Locks & Keys"]],
        [
          "Buildings Combined",
          "Malicious Damage",
          ["Malicious Damage", "Malicious Damage With Theft"]
        ],
        ["Buildings Combined", "Riot & Strike", ["NASRIA", "Outside RSA / NAM", "SASRIA"]],
        [
          "Buildings Combined",
          "Storm",
          ["Hail / Snow", "Lightning", "Storm / Wind", "Water / Flood"]
        ],
        ["Buildings Combined", "Subsidence & Landslip", ["Extended", "Limited"]],
        //Business All Risks
        [
          "Business All Risks",
          "Accidental Damage",
          ["Accidental Damage General", "Lost / Misplaced"]
        ],
        ["Business All Risks", "Earthquake", ["Mining Operations", "Natural Phenomenon"]],
        ["Business All Risks", "Explosion", ["Explosion"]],
        [
          "Business All Risks",
          "Fire",
          [
            "Electrical Fault",
            "Explosion",
            "External source",
            "Fire General",
            "Mechanical Fault",
            "Poor Housekeeping / Lack of Care",
            "Source off Premises",
            "Spontaneous Combustion",
            "Subterranean"
          ]
        ],
        [
          "Business All Risks",
          "Leakage",
          [
            "Chemicals / Oils / Fumes",
            "Water - from Fire Extinguishing Installations",
            "Water - from Pipes"
          ]
        ],
        [
          "Business All Risks",
          "Malicious Damage",
          ["Malicious Damage", "Malicious Damage With Theft"]
        ],
        ["Business All Risks", "Riot & Strike", ["NASRIA", "Outside RSA / NAM", "SASRIA"]],
        [
          "Business All Risks",
          "Storm",
          ["Hail / Snow", "Lightning", "Storm / Wind", "Water / Flood"]
        ],
        ["Business All Risks", "Subsidence & Landslip", ["Extended", "Limited"]],
        //Business Interruption

        [
          "Business Interruption",
          "Accidental Damage",
          ["Accidental Damage General", "Deterioration of Stock"]
        ],
        ["Business Interruption", "Earthquake", ["Mining Operations", "Natural Phenomenon"]],
        ["Business Interruption", "Explosion", ["Explosion"]],
        [
          "Business Interruption",
          "Fire",
          [
            "Electrical Fault",
            "Explosion",
            "External source",
            "Fire General",
            "Mechanical Fault",
            "Poor Housekeeping / Lack of Care",
            "Source off Premises",
            "Spontaneous Combustion",
            "Subterranean"
          ]
        ],
        [
          "Business Interruption",
          "Leakage",
          [
            "Chemicals / Oils / Fumes",
            "Water - from Fire Extinguishing Installations",
            "Water - from Pipes"
          ]
        ],
        [
          "Business Interruption",
          "Malicious Damage",
          ["Malicious Damage", "Malicious Damage With Theft"]
        ],
        ["Business Interruption", "Riot & Strike", ["NASRIA", "Outside RSA / NAM", "SASRIA"]],
        [
          "Business Interruption",
          "Storm",
          ["Hail / Snow", "Lightning", "Storm / Wind", "Water / Flood"]
        ],
        ["Business Interruption", "Subsidence & Landslip", ["Extended", "Limited"]],
        //Contract Works
        [
          "Contract Works",
          "Accidental Damage",
          ["Accidental Damage General", "In Transit", "Underground Systems"]
        ],
        ["Contract Works", "Earthquake", ["Mining Operations", "Natural Phenomenon"]],
        ["Contract Works", "Explosion", ["Explosion"]],
        [
          "Contract Works",
          "Fire",
          [
            "Electrical Fault",
            "Explosion",
            "External source",
            "Fire General",
            "Mechanical Fault",
            "Poor Housekeeping / Lack of Care",
            "Source off Premises",
            "Spontaneous Combustion",
            "Subterranean"
          ]
        ],
        ["Contract Works", "Malicious Damage", ["Malicious Damage", "Malicious Damage With Theft"]],
        ["Contract Works", "Riot & Strike", ["NASRIA", "Outside RSA / NAM", "SASRIA"]],
        ["Contract Works", "Storm", ["Hail / Snow", "Lightning", "Storm / Wind", "Water / Flood"]],
        ["Contract Works", "Subsidence & Landslip", ["Extended", "Limited"]],
        //Electronic Equipment
        [
          "Electronic Equipment",
          "Accidental Damage",
          [
            "Accidental Damage General",
            "In Transit",
            "Impact by Aircraft / Aerial Devices",
            "Impact by Animals / Trees / Vehicles",
            "Power Surge"
          ]
        ],
        ["Electronic Equipment", "Earthquake", ["Mining Operations", "Natural Phenomenon"]],
        ["Electronic Equipment", "Explosion", ["Explosion"]],
        [
          "Electronic Equipment",
          "Fire",
          [
            "Electrical Fault",
            "Explosion",
            "External source",
            "Fire General",
            "Mechanical Fault",
            "Poor Housekeeping / Lack of Care",
            "Source off Premises",
            "Spontaneous Combustion",
            "Subterranean"
          ]
        ],
        [
          "Electronic Equipment",
          "Leakage",
          [
            "Chemicals / Oils / Fumes",
            "Water - from Fire Extinguishing Installations",
            "Water - from Pipes"
          ]
        ],
        [
          "Electronic Equipment",
          "Malicious Damage",
          ["Malicious Damage", "Malicious Damage With Theft"]
        ],
        ["Electronic Equipment", "Riot & Strike", ["NASRIA", "Outside RSA / NAM", "SASRIA"]],
        [
          "Electronic Equipment",
          "Storm",
          ["Hail / Snow", "Lightning", "Storm / Wind", "Water / Flood"]
        ],
        ["Electronic Equipment", "Subsidence & Landslip", ["Extended", "Limited"]],
        //Employers Liability
        [
          "Employers Liability",
          "Death",
          [
            "Accidental",
            "Building / Premises",
            "Assault",
            "Machinery / Equipment",
            "Road Accident",
            "Toxic Processes",
            "Unhygienic Conditions"
          ]
        ],
        [
          "Employers Liability",
          "Illness",
          [
            "Accidental",
            "Building / Premises",
            "Assault",
            "Machinery / Equipment",
            "Road Accident",
            "Toxic Processes",
            "Unhygienic Conditions"
          ]
        ],
        [
          "Employers Liability",
          "Injury",
          [
            "Accidental",
            "Building / Premises",
            "Assault",
            "Machinery / Equipment",
            "Road Accident",
            "Toxic Processes",
            "Unhygienic Conditions"
          ]
        ],
        //Fidelity Guarantee
        ["Fidelity Guarantee", "Theft", ["Employee Fidelity"]],
        //Fire
        [
          "Fire",
          "Accidental Damage",
          [
            "Impact by Aircraft / Aerial Devices",
            "Impact by Animals / Trees / Vehicles",
            "Power Surge",
            "Public Supply Connections",
            "Wild Animals Damage"
          ]
        ],
        ["Fire", "Earthquake", ["Mining Operations", "Natural Phenomenon"]],
        ["Fire", "Explosion", ["Explosion"]],
        [
          "Fire",
          "Fire",
          [
            "Electrical Fault",
            "Explosion",
            "External source",
            "Fire General",
            "Mechanical Fault",
            "Poor Housekeeping / Lack of Care",
            "Source off Premises",
            "Spontaneous Combustion",
            "Subterranean"
          ]
        ],
        ["Fire", "Geyser", ["Repair", "Replace", "With Resultant Water Damage"]],
        ["Fire", "Leakage", ["Water - from Fire Extinguishing Installations"]],
        ["Fire", "Malicious Damage", ["Malicious Damage", "Malicious Damage With Theft"]],
        ["Fire", "Riot & Strike", ["NASRIA", "Outside RSA / NAM", "SASRIA"]],
        ["Fire", "Storm", ["Hail / Snow", "Lightning", "Storm / Wind", "Water / Flood"]],
        ["Fire", "Subsidence & Landslip", ["Extended", "Limited"]],

        //Glass
        ["Glass", "Accidental Damage", ["Accidental Damage General", "Glass Buildings"]],
        ["Glass", "Malicious Damage", ["Malicious Damage", "Malicious Damage With Theft"]],
        ["Glass", "Riot & Strike", ["NASRIA", "Outside RSA / NAM", "SASRIA"]],
        //Goods In Transit
        [
          "Goods In Transit",
          "Accidental Damage",
          [
            "Accidental Damage General",
            "GIT Collission / Overturning / Derailment",
            "GIT Falling off conveyance"
          ]
        ],
        ["Goods In Transit", "Earthquake", ["Mining Operations", "Natural Phenomenon"]],
        ["Goods In Transit", "Explosion", ["Explosion"]],
        [
          "Goods In Transit",
          "Fire",
          [
            "Electrical Fault",
            "Explosion",
            "External source",
            "Fire General",
            "Mechanical Fault",
            "Poor Housekeeping / Lack of Care",
            "Source off Premises",
            "Spontaneous Combustion",
            "Subterranean"
          ]
        ],
        [
          "Goods In Transit",
          "Malicious Damage",
          ["Malicious Damage", "Malicious Damage With Theft"]
        ],
        ["Goods In Transit", "Riot & Strike", ["NASRIA", "Outside RSA / NAM", "SASRIA"]],
        [
          "Goods In Transit",
          "Storm",
          ["Hail / Snow", "Lightning", "Storm / Wind", "Water / Flood"]
        ],
        ["Goods In Transit", "Subsidence & Landslip", ["Extended", "Limited"]],
        //Group Personal Accident
        [
          "Group Personal Accident",
          "Death",
          [
            "Accidental",
            "Building / Premises",
            "Assault",
            "Machinery / Equipment",
            "Passive War",
            "Road Accident",
            "Toxic Processes",
            "Unhygienic Conditions"
          ]
        ],
        [
          "Group Personal Accident",
          "Illness",
          [
            "Accidental",
            "Building / Premises",
            "Assault",
            "Machinery / Equipment",
            "Road Accident",
            "Passive War",
            "Toxic Processes",
            "Unhygienic Conditions"
          ]
        ],
        [
          "Group Personal Accident",
          "Injury",
          [
            "Accidental",
            "Building / Premises",
            "Assault",
            "Machinery / Equipment",
            "Passive War",
            "Road Accident",
            "Toxic Processes",
            "Unhygienic Conditions"
          ]
        ],
        ["Group Personal Accident", "Medical Expenses", ["Accidental", "Assault", "Road Accident"]],
        //Homeowners
        [
          "Homeowners",
          "Accidental Damage",
          [
            "Accidental Damage General",
            "Aerial Fittings",
            "Domestic Machinery",
            "Glass Buildings",
            "Glass Sanitaryware",
            "Impact by Aircraft / Aerial Devices",
            "Impact by Animals / Trees / Vehicles",
            "Public Supply Connections",
            "Tree Removal Costs",
            "Wild Animals Damage"
          ]
        ],
        ["Homeowners", "Earthquake", ["Mining Operations", "Natural Phenomenon"]],
        ["Homeowners", "Explosion", ["Explosion"]],
        [
          "Homeowners",
          "Fire",
          [
            "Electrical Fault",
            "Explosion",
            "External source",
            "Fire General",
            "Mechanical Fault",
            "Poor Housekeeping / Lack of Care",
            "Source off Premises",
            "Spontaneous Combustion",
            "Subterranean"
          ]
        ],
        [
          "Homeowners",
          "Leakage",
          [
            "Chemicals / Oils / Fumes",
            "Water - from Fire Extinguishing Installations",
            "Water - from Pipes",
            "Water - Leakage Costs"
          ]
        ],
        ["Homeowners", "Locks & Keys", ["Locks & Keys"]],
        ["Homeowners", "Malicious Damage", ["Malicious Damage", "Malicious Damage With Theft"]],
        ["Homeowners", "Riot & Strike", ["NASRIA", "Outside RSA / NAM", "SASRIA"]],
        ["Homeowners", "Storm", ["Hail / Snow", "Lightning", "Storm / Wind", "Water / Flood"]],
        ["Homeowners", "Subsidence & Landslip", ["Extended", "Limited"]],
        [
          "Homeowners",
          "Theft",
          [
            "From Premises",
            "From Unattended Vehicle",
            "General",
            "Hijack",
            "Temporarily Removed from Building"
          ]
        ],
        //House Holders
        [
          "House Holders",
          "Accidental Damage",
          [
            "Accidental Damage General",
            "Aerial Fittings",
            "Audio Visual Equipment",
            "Deterioration of Stock",
            "Domestic Machinery",
            "Glass Furniture",
            "Impact by Aircraft / Aerial Devices",
            "Impact by Animals / Trees / Vehicles",
            "In Transit",
            "Power Surge"
          ]
        ],
        ["House Holders", "Earthquake", ["Mining Operations", "Natural Phenomenon"]],
        ["House Holders", "Explosion", ["Explosion"]],
        ["House Holders", "Death", ["Accidental"]],
        ["House Holders", "Injury", ["Accidental"]],
        [
          "House Holders",
          "Fire",
          [
            "Electrical Fault",
            "Explosion",
            "External source",
            "Fire General",
            "Mechanical Fault",
            "Poor Housekeeping / Lack of Care",
            "Source off Premises",
            "Spontaneous Combustion",
            "Subterranean"
          ]
        ],
        [
          "House Holders",
          "Leakage",
          [
            "Chemicals / Oils / Fumes",
            "Water - from Fire Extinguishing Installations",
            "Water - from Pipes",
            "Water - Leakage Costs"
          ]
        ],
        ["House Holders", "Geyser", ["Repair", "Replace", "With Resultant Water Damage"]],
        ["House Holders", "Locks & Keys", ["Locks & Keys"]],
        ["House Holders", "Malicious Damage", ["Malicious Damage", "Malicious Damage With Theft"]],
        [
          "House Holders",
          "Machinery Breakdown",
          ["Electrical Breakdown", "Electronical Breakdown", "Mechanical Breakdown"]
        ],
        ["House Holders", "Medical Expenses", ["Accidental"]],
        ["House Holders", "Riot & Strike", ["NASRIA", "Outside RSA / NAM", "SASRIA"]],
        ["House Holders", "Storm", ["Hail / Snow", "Lightning", "Storm / Wind", "Water / Flood"]],
        ["House Holders", "Subsidence & Landslip", ["Extended", "Limited"]],
        ["House Holders", "Theft", ["From Unattended Vehicle"]],
        //money

        //Machinery Breakdown
        [
          "Machinery Breakdown",
          "Accidental Damage",
          ["Accidental Damage General", "Deterioration of Stock", "Power Surge"]
        ],
        ["Machinery Breakdown", "Explosion", ["Explosion"]],
        ["Machinery Breakdown", "Fire", ["Electrical Fault", "Explosion"]],
        [
          "Machinery Breakdown",
          "Machinery Breakdown",
          [
            "Centrifugal forces",
            "Electrical Breakdown",
            "Electronical Breakdown",
            "Excessive Electrical Pressure",
            "Faulty Casting, Construction, Erection",
            "Faulty Design",
            "Faulty Material",
            "Faulty Workmanship, Lack of Skill, Carelessness",
            "Loosening of Parts",
            "Mechanical Breakdown",
            "Obstruction or entry of foreign bodies",
            "Short Circuit",
            "Shortage of Water in Boilers",
            "Vibration, mal-adjustment, mal-alignment"
          ]
        ],

        //Money
        ["Money", "Injury", ["Assault"]],
        ["Money", "Medical Expenses", ["Assault"]],
        ["Money", "Locks & Keys", ["Locks & Keys"]],
        [
          "Money",
          "Money Loss",
          [
            "At Insured Premises During Business Hours",
            "At Insured Premises Outside Business Hours",
            "Cheque Fraud",
            "In the Custody of a Collector / Roundsman",
            "In the Custody of a Petrol Attendant",
            "In Transit By Employee(s)",
            "In Transit By Security Firm",
            "Official on Business Trip",
            "Officials Residence"
          ]
        ],
        ["Money", "Riot & Strike", ["NASRIA", "Outside RSA / NAM", "SASRIA"]],
        ["Money", "Death", ["Assault"]],
        //Office Contents
        [
          "Office Contents",
          "Accidental Damage",
          [
            "Accidental Damage General",
            "Documents",
            "Glass Furniture",
            "Impact by Aircraft / Aerial Devices",
            "Impact by Animals / Trees / Vehicles",
            "Power Surge"
          ]
        ],
        ["Office Contents", "Earthquake", ["Mining Operations", "Natural Phenomenon"]],
        ["Office Contents", "Explosion", ["Explosion"]],
        [
          "Office Contents",
          "Fire",
          [
            "Electrical Fault",
            "Explosion",
            "External source",
            "Fire General",
            "Mechanical Fault",
            "Poor Housekeeping / Lack of Care",
            "Source off Premises",
            "Spontaneous Combustion",
            "Subterranean"
          ]
        ],
        ["Office Contents", "Geyser", ["Repair", "Replace", "With Resultant Water Damage"]],
        [
          "Office Contents",
          "Leakage",
          [
            "Chemicals / Oils / Fumes",
            "Water - from Fire Extinguishing Installations",
            "Water - from Pipes"
          ]
        ],
        ["Office Contents", "Locks & Keys", ["Locks & Keys"]],
        [
          "Office Contents",
          "Malicious Damage",
          ["Malicious Damage", "Malicious Damage With Theft"]
        ],
        ["Office Contents", "Riot & Strike", ["NASRIA", "Outside RSA / NAM", "SASRIA"]],
        ["Office Contents", "Storm", ["Hail / Snow", "Lightning", "Storm / Wind", "Water / Flood"]],
        ["Office Contents", "Subsidence & Landslip", ["Extended", "Limited"]],
        //Personal Liability
        [
          "Personal Liability",
          "CUP/PLIP",
          [
            "General - Additional Risks",
            "General - Difference in Conditions",
            "General - Excess Layer",
            "Motor - Additional Risks",
            "Motor - Difference in Conditions",
            "Motor - Excess Layer",
            "Property Owners - Additional Risks",
            "Property Owners - Difference in Conditions",
            "Property Owners - Excess Layer",
            "Tenants - Additional Risks",
            "Tenants - Difference in Conditions",
            "Tenants - Excess Layer",
            "Watercraft - Additional Risks",
            "Watercraft - Difference in Conditions",
            "Watercraft - Excess Layer"
          ]
        ],
        ["Personal Liability", "Death", ["Accidental"]],
        ["Personal Liability", "Injury", ["Accidental"]],
        ["Personal Liability", "Illness", ["Accidental"]],
        [
          "Personal Liability",
          "Liability",
          [
            "Bowls Full-House",
            "First Aid",
            "General",
            "Golfers Hole-in-One",
            "Lawn Mowers & Gardening Tools",
            "Neighbourhood Watch",
            "Security Firm",
            "Tenants Liability"
          ]
        ],

        //Plant
        [
          "Plant",
          "Accidental Damage",
          [
            "Accidental Damage General",
            "GIT Collission / Overturning / Derailment",
            "GIT Falling off conveyance",
            "Impact by Aircraft / Aerial Devices",
            "Impact by Animals / Trees / Vehicles",
            "In Transit"
          ]
        ],
        ["Plant", "Earthquake", ["Mining Operations", "Natural Phenomenon"]],
        ["Plant", "Explosion", ["Explosion"]],
        [
          "Plant",
          "Fire",
          [
            "Electrical Fault",
            "Explosion",
            "External source",
            "Fire General",
            "Mechanical Fault",
            "Poor Housekeeping / Lack of Care",
            "Source off Premises",
            "Spontaneous Combustion",
            "Subterranean"
          ]
        ],
        ["Plant", "Liability", ["General"]],
        ["Plant", "Malicious Damage", ["Malicious Damage", "Malicious Damage With Theft"]],
        ["Plant", "Riot & Strike", ["NASRIA", "Outside RSA / NAM", "SASRIA"]],
        ["Plant", "Storm", ["Hail / Snow", "Lightning", "Storm / Wind", "Water / Flood"]],

        //Public Liability
        [
          "Public Liability",
          "CUP/PLIP",
          [
            "General - Additional Risks",
            "General - Difference in Conditions",
            "General - Excess Layer",
            "Motor - Additional Risks",
            "Motor - Difference in Conditions",
            "Motor - Excess Layer",
            "Property Owners - Additional Risks",
            "Property Owners - Difference in Conditions",
            "Property Owners - Excess Layer",
            "Tenants - Additional Risks",
            "Tenants - Difference in Conditions",
            "Tenants - Excess Layer",
            "Watercraft - Additional Risks",
            "Watercraft - Difference in Conditions",
            "Watercraft - Excess Layer"
          ]
        ],
        ["Public Liability", "Death", ["Accidental"]],
        ["Public Liability", "Injury", ["Accidental"]],
        ["Public Liability", "Illness", ["Accidental"]],
        [
          "Public Liability",
          "Liability",
          [
            "Animals - Auctions",
            "Animals - Driven / Escaped / Stray",
            "Animals - Grazing (not Insureds)",
            "Animals - Vehicles Drawn by",
            "Beauty Salon",
            "Breaking / Overflowing of Walls / Dams",
            "Car Parks",
            "Car Wash & Valet",
            "Chemist Prescription",
            "Crop Spraying",
            "Defective Workmanship",
            "Fire Spread - Other Than Sugar-Cane and Plantations",
            "Fire Spread - Sugar-Cane and Plantations",
            "First Aid",
            "Forecourt Service",
            "Game Hunting & Viewing",
            "General",
            "Government & Other Authorities Agreements",
            "Gratuitous Advice",
            "Hairdressers",
            "Hotel - Goods In Custody",
            "Lawn Mowers & Gardening Tools",
            "Neighbourhood Watch",
            "Products - Exported to E.E.C. Countries",
            "Products - Exported to Other Countries",
            "Products - Exported to U.S.A / Canada",
            "Products - General",
            "Products - Guarantee",
            "Products - Inefficacy",
            "Products - Recall",
            "Professional",
            "Schools",
            "Security Firm",
            "Statutory Legal Defence Costs",
            "Tenants Liability",
            "Tool of Trade",
            "Unattached Trailers",
            "Visitors & Employees Property",
            "Warehousemens Liability",
            "Work Away from Insured Premises",
            "Wrongful Act - Arrest & Defamation",
            "Wrongful Act - Directors",
            "Wrongful Act - Trustee"
          ]
        ],

        //Special Risks
        [
          "Special Risks",
          "Accidental Damage",
          [
            "Accidental Damage General",
            "Glass Buildings",
            "Impact by Aircraft / Aerial Devices",
            "Impact by Animals / Trees / Vehicles",
            "Power Surge",
            "Public Supply Connections",
            "Wild Animals Damage"
          ]
        ],
        ["Special Risks", "Claims Preparation Costs", ["Claims Preparation Costs"]],
        [
          "Special Risks",
          "CUP/PLIP",
          [
            "General - Additional Risks",
            "General - Difference in Conditions",
            "General - Excess Layer",
            "Motor - Additional Risks",
            "Motor - Difference in Conditions",
            "Motor - Excess Layer",
            "Property Owners - Additional Risks",
            "Property Owners - Difference in Conditions",
            "Property Owners - Excess Layer",
            "Tenants - Additional Risks",
            "Tenants - Difference in Conditions",
            "Tenants - Excess Layer",
            "Watercraft - Additional Risks",
            "Watercraft - Difference in Conditions",
            "Watercraft - Excess Layer"
          ]
        ],
        ["Special Risks", "Director & Officers Indemnity", ["Director & Officers Indemnity"]],
        ["Special Risks", "Earthquake", ["Mining Operations", "Natural Phenomenon"]],
        ["Special Risks", "Geyser", ["Repair", "Replace", "With Resultant Water Damage"]],
        ["Special Risks", "Landscaped Gardens", ["Landscaped Gardens"]],
        [
          "Special Risks",
          "Leakage",
          [
            "Chemicals / Oils / Fumes",
            "Water - from Fire Extinguishing Installations",
            "Water - from Pipes",
            "Water - Leakage Costs"
          ]
        ],
        ["Special Risks", "Locks & Keys", ["Locks & Keys"]],
        ["Special Risks", "Malicious Damage", ["Malicious Damage", "Malicious Damage With Theft"]],
        ["Special Risks", "Riot & Strike", ["NASRIA", "Outside RSA / NAM", "SASRIA"]],
        ["Special Risks", "Storm", ["Hail / Snow", "Lightning", "Storm / Wind", "Water / Flood"]],
        ["Special Risks", "Excess buy back", ["Excess buy back"]],
        ["Special Risks", "Explosion", ["Explosion"]],
        ["Special Risks", "Subsidence & Landslip", ["Extended", "Limited"]],
        [
          "Special Risks",
          "Power Surge Cover to Non Electronic Equipment",
          ["Power Surge Cover to Non Electronic Equipment"]
        ],
        ["Special Risks", "Security Services", ["Security Services"]],
        [
          "Special Risks",
          "Theft",
          [
            "From Premises",
            "From Unattended Vehicle",
            "General",
            "Hijack",
            "Temporarily Removed from Building"
          ]
        ],
        [
          "Special Risks",
          "Vehicle - Accident",
          [
            "Animals / Trees",
            "Falling Objects",
            "Fixed Property",
            "Other vehicle(s)",
            "Parked - damaged by TP",
            "Pedestrian / Cyclist",
            "Potholes / Inappropriate road surface",
            "Single Vehicle Accident"
          ]
        ],
        ["Special Risks", "Vehicle - Glass", ["Repair", "Replace"]],
        [
          "Special Risks",
          "Vehicle - Roadside Assistance",
          [
            "Electrical Breakdown",
            "Flat Battery / Tyre",
            "Keys locked in vehicle",
            "Mechanical Breakdown"
          ]
        ],
        [
          "Special Risks",
          "Vehicle - Theft",
          [
            "Equipment & Damage to vehicle",
            "Equipment & No damage to vehicle",
            "Vehicle - Abscondence",
            "Vehicle - Hijack",
            "Vehicle - Theft"
          ]
        ],
        ["Special Risks", "Waiver of basic excess only", ["Waiver of basic excess only"]],
        ["Special Risks", "Wear and Tear on Internal Pipes", ["Wear and Tear on Internal Pipes"]],
        [
          "Special Risks",
          "Fire",
          [
            "Electrical Fault",
            "Explosion",
            "External source",
            "Fire General",
            "Mechanical Fault",
            "Poor Housekeeping / Lack of Care",
            "Source off Premises",
            "Spontaneous Combustion",
            "Subterranean"
          ]
        ],
        //Stated Benefits
        [
          "Stated Benefits",
          "Death",
          [
            "Accidental",
            "Building / Premises",
            "Assault",
            "Machinery / Equipment",
            "Road Accident",
            "Toxic Processes",
            "Unhygienic Conditions",
            "Passive War"
          ]
        ],
        [
          "Stated Benefits",
          "Illness",
          [
            "Accidental",
            "Building / Premises",
            "Assault",
            "Machinery / Equipment",
            "Road Accident",
            "Toxic Processes",
            "Unhygienic Conditions",
            "Passive War"
          ]
        ],
        [
          "Stated Benefits",
          "Injury",
          [
            "Accidental",
            "Building / Premises",
            "Assault",
            "Machinery / Equipment",
            "Road Accident",
            "Toxic Processes",
            "Unhygienic Conditions"
          ]
        ],
        ["Stated Benefits", "Medical Expenses", ["Accidental", "Assault", "Road Accident"]],
        //Theft
        ["Theft", "Locks & Keys", ["Locks & Keys"]],

        //Watercraft
        [
          "Watercraft",
          "Accidental Damage",
          [
            "Accidental Damage General",
            "GIT Collission / Overturning / Derailment",
            "GIT Falling off conveyance",
            "Impact by Aircraft / Aerial Devices",
            "In Transit"
          ]
        ],
        ["Watercraft", "Earthquake", ["Mining Operations", "Natural Phenomenon"]],
        ["Watercraft", "Explosion", ["Explosion"]],
        [
          "Watercraft",
          "Fire",
          [
            "Electrical Fault",
            "Explosion",
            "External source",
            "Fire General",
            "Mechanical Fault",
            "Poor Housekeeping / Lack of Care",
            "Source off Premises",
            "Spontaneous Combustion",
            "Subterranean"
          ]
        ],
        ["Watercraft", "Liability", ["General"]],
        ["Watercraft", "Malicious Damage", ["Malicious Damage", "Malicious Damage With Theft"]],
        [
          "Watercraft",
          "Marine",
          [
            "Accidental Damage",
            "Capsizing",
            "Force of Nature",
            "Impact Damage",
            "Machinery Damage",
            "Malicious Damage",
            "Outboards Overboard",
            "Pirating",
            "Repairs Negligence",
            "Shortages / Non delivery of Cargo",
            "Sinking",
            "Stranding",
            "Submerged Object Struck",
            "War"
          ]
        ],
        ["Watercraft", "Medical Expenses", ["Accidental"]],
        ["Watercraft", "Riot & Strike", ["NASRIA", "Outside RSA / NAM", "SASRIA"]],
        ["Watercraft", "Storm", ["Hail / Snow", "Lightning", "Storm / Wind", "Water / Flood"]]
      ],
      schemaIndex: 1,
      action: () => removeSchemasByTitle("Theft")
    },
    //Police
    {
      causes: [
        //"Accidental Damage"
        [
          "Accidental Damage",
          "Accidental Damage",
          [
            "Accidental Damage General",
            "Aerial Fittings",
            "Impact by Aircraft / Aerial Devices",
            "Impact by Animals / Trees / Vehicles",
            "Lost / Misplaced",
            "Power Surge"
          ]
        ],
        [
          "Accidental Damage",
          "Leakage",
          [
            "Chemicals / Oils / Fumes",
            "Water - from Fire Extinguishing Installations",
            "Water - from Pipes"
          ]
        ],
        [
          "Accidental Damage",
          "Accidental Damage - Wine",
          [
            "Breakage - Bottled Wine",
            "Breakage - Bottles",
            "Breakage - Tanks",
            "Contamination",
            "Fermentation",
            "Latent Defect",
            "Leakage - Stock",
            "Leakage - Surrounding Property - Not Wine",
            "Leakage - Surrounding Property - Wine",
            "Loading / Off Loading"
          ]
        ],
        //Accounts Receivable
        ["Accounts Receivable", "Accidental Damage", ["Accidental Damage General"]],
        ["Accounts Receivable", "Explosion", ["Explosion"]],
        [
          "Accounts Receivable",
          "Fire",
          [
            "Electrical Fault",
            "Explosion",
            "External source",
            "Fire General",
            "Mechanical Fault",
            "Poor Housekeeping / Lack of Care",
            "Source off Premises",
            "Spontaneous Combustion",
            "Subterranean"
          ]
        ],
        ["Accounts Receivable", "Earthquake", ["Mining Operations", "Natural Phenomenon"]],
        [
          "Accounts Receivable",
          "Leakage",
          [
            "Chemicals / Oils / Fumes",
            "Water - from Pipes",
            "Water - from Fire Extinguishing Installations"
          ]
        ],
        [
          "Accounts Receivable",
          "Malicious Damage",
          ["Malicious Damage", "Malicious Damage With Theft"]
        ],
        ["Accounts Receivable", "Riot & Strike", ["NASRIA", "Outside RSA / NAM", "SASRIA"]],
        [
          "Accounts Receivable",
          "Storm",
          ["Hail / Snow", "Lightning", "Storm / Wind", "Water / Flood"]
        ],
        ["Accounts Receivable", "Subsidence & Landslip", ["Extended", "Limited"]],

        //all risk
        ["All Risk", "Accidental Damage", ["Accidental Damage General", "Lost / Misplaced"]],
        ["All Risk", "Earthquake", ["Mining Operations", "Natural Phenomenon"]],
        ["All Risk", "Explosion", ["Explosion"]],
        [
          "All Risk",
          "Fire",
          [
            "Electrical Fault",
            "Explosion",
            "External source",
            "Fire General",
            "Mechanical Fault",
            "Poor Housekeeping / Lack of Care",
            "Source off Premises",
            "Spontaneous Combustion",
            "Subterranean"
          ]
        ],
        [
          "All Risk",
          "Leakage",
          [
            "Chemicals / Oils / Fumes",
            "Water - from Fire Extinguishing Installations",
            "Water - from Pipes"
          ]
        ],
        ["All Risk", "Malicious Damage", ["Malicious Damage", "Malicious Damage With Theft"]],
        ["All Risk", "Riot & Strike", ["NASRIA", "Outside RSA / NAM", "SASRIA"]],
        ["All Risk", "Storm", ["Hail / Snow", "Lightning", "Storm / Wind", "Water / Flood"]],
        ["All Risk", "Subsidence & Landslip", ["Extended", "Limited"]],
        [
          "All Risk",
          "Theft",
          [
            "From Premises",
            "From Unattended Vehicle",
            "General",
            "Hijack",
            "Temporarily Removed from Building",
            "With Force",
            "Without Force"
          ]
        ],
        //body corporate
        [
          "Body Corporate",
          "Accidental Damage",
          [
            "Aerial Fittings",
            "Accidental Damage General",
            "Domestic Machinery",
            "Glass Buildings",
            "Glass Sanitaryware",
            "Impact by Aircraft / Aerial Devices",
            "Impact by Animals / Trees / Vehicles",
            "Power Surge",
            "Public Supply Connections",
            "Tree Removal Costs",
            "Wild Animals Damage"
          ]
        ],
        ["Body Corporate", "Death", ["Accidental", "Building / Premises"]],
        ["Body Corporate", "Earthquake", ["Mining Operations", "Natural Phenomenon"]],
        ["Body Corporate", "Explosion", ["Explosion"]],
        [
          "Body Corporate",
          "Fire",
          [
            "Electrical Fault",
            "Explosion",
            "External source",
            "Fire General",
            "Mechanical Fault",
            "Poor Housekeeping / Lack of Care",
            "Source off Premises",
            "Spontaneous Combustion",
            "Subterranean"
          ]
        ],
        [
          "Body Corporate",
          "Geyser",
          ["Repair", "Replacement", "With Resultant Water Damage", "Replace"]
        ],
        ["Body Corporate", "Illness", ["Accidental", "Building / Premises"]],
        ["Body Corporate", "Injury", ["Accidental", "Building / Premises"]],
        [
          "Body Corporate",
          "Leakage",
          [
            "Chemicals / Oils / Fumes",
            "Water - from Fire Extinguishing Installations",
            "Water - from Pipes",
            "Water - Leakage Costs"
          ]
        ],
        ["Body Corporate", "Liability", ["General", "Security Firm"]],
        ["Body Corporate", "Locks & Keys", ["Locks & Keys"]],
        ["Body Corporate", "Malicious Damage", ["Malicious Damage", "Malicious Damage With Theft"]],
        ["Body Corporate", "Riot & Strike", ["NASRIA", "Outside RSA / NAM", "SASRIA"]],
        ["Body Corporate", "Storm", ["Hail / Snow", "Lightning", "Storm / Wind", "Water / Flood"]],
        ["Body Corporate", "Subsidence & Landslip", ["Extended", "Limited"]],
        [
          "Body Corporate",
          "Theft",
          ["From Premises", "From Unattended Vehicle", "General", "With Force", "Without Force"]
        ],
        //Buildings Combined
        [
          "Buildings Combined",
          "Accidental Damage",
          [
            "Aerial Fittings",
            "Accidental Damage General",
            "Domestic Machinery",
            "Glass Buildings",
            "Glass Sanitaryware",
            "Impact by Aircraft / Aerial Devices",
            "Impact by Animals / Trees / Vehicles",
            "Power Surge",
            "Public Supply Connections",
            "Tree Removal Costs",
            "Wild Animals Damage"
          ]
        ],
        ["Buildings Combined", "Death", ["Accidental", "Building / Premises"]],
        ["Buildings Combined", "Earthquake", ["Mining Operations", "Natural Phenomenon"]],
        ["Buildings Combined", "Explosion", ["Explosion"]],
        [
          "Buildings Combined",
          "Fire",
          [
            "Electrical Fault",
            "Explosion",
            "External source",
            "Fire General",
            "Mechanical Fault",
            "Poor Housekeeping / Lack of Care",
            "Source off Premises",
            "Spontaneous Combustion",
            "Subterranean"
          ]
        ],
        ["Buildings Combined", "Geyser", ["Repair", "Replacement", "With Resultant Water Damage"]],
        ["Buildings Combined", "Illness", ["Accidental", "Building / Premises"]],
        ["Buildings Combined", "Injury", ["Accidental", "Building / Premises"]],
        [
          "Buildings Combined",
          "Leakage",
          [
            "Chemicals / Oils / Fumes",
            "Water - from Fire Extinguishing Installations",
            "Water - from Pipes",
            "Water - Leakage Costs"
          ]
        ],
        ["Buildings Combined", "Liability", ["General", "Security Firm"]],
        ["Buildings Combined", "Locks & Keys", ["Locks & Keys"]],
        [
          "Buildings Combined",
          "Malicious Damage",
          ["Malicious Damage", "Malicious Damage With Theft"]
        ],
        ["Buildings Combined", "Riot & Strike", ["NASRIA", "Outside RSA / NAM", "SASRIA"]],
        [
          "Buildings Combined",
          "Storm",
          ["Hail / Snow", "Lightning", "Storm / Wind", "Water / Flood"]
        ],
        ["Buildings Combined", "Subsidence & Landslip", ["Extended", "Limited"]],
        //Business All Risks
        [
          "Business All Risks",
          "Accidental Damage",
          ["Accidental Damage General", "Lost / Misplaced"]
        ],
        ["Business All Risks", "Earthquake", ["Mining Operations", "Natural Phenomenon"]],
        ["Business All Risks", "Explosion", ["Explosion"]],
        [
          "Business All Risks",
          "Fire",
          [
            "Electrical Fault",
            "Explosion",
            "External source",
            "Fire General",
            "Mechanical Fault",
            "Poor Housekeeping / Lack of Care",
            "Source off Premises",
            "Spontaneous Combustion",
            "Subterranean"
          ]
        ],
        [
          "Business All Risks",
          "Leakage",
          [
            "Chemicals / Oils / Fumes",
            "Water - from Fire Extinguishing Installations",
            "Water - from Pipes"
          ]
        ],
        [
          "Business All Risks",
          "Malicious Damage",
          ["Malicious Damage", "Malicious Damage With Theft"]
        ],
        ["Business All Risks", "Riot & Strike", ["NASRIA", "Outside RSA / NAM", "SASRIA"]],
        [
          "Business All Risks",
          "Storm",
          ["Hail / Snow", "Lightning", "Storm / Wind", "Water / Flood"]
        ],
        ["Business All Risks", "Subsidence & Landslip", ["Extended", "Limited"]],
        //Business Interruption

        [
          "Business Interruption",
          "Accidental Damage",
          ["Accidental Damage General", "Deterioration of Stock"]
        ],
        ["Business Interruption", "Earthquake", ["Mining Operations", "Natural Phenomenon"]],
        ["Business Interruption", "Explosion", ["Explosion"]],
        [
          "Business Interruption",
          "Fire",
          [
            "Electrical Fault",
            "Explosion",
            "External source",
            "Fire General",
            "Mechanical Fault",
            "Poor Housekeeping / Lack of Care",
            "Source off Premises",
            "Spontaneous Combustion",
            "Subterranean"
          ]
        ],
        [
          "Business Interruption",
          "Leakage",
          [
            "Chemicals / Oils / Fumes",
            "Water - from Fire Extinguishing Installations",
            "Water - from Pipes"
          ]
        ],
        [
          "Business Interruption",
          "Malicious Damage",
          ["Malicious Damage", "Malicious Damage With Theft"]
        ],
        ["Business Interruption", "Riot & Strike", ["NASRIA", "Outside RSA / NAM", "SASRIA"]],
        [
          "Business Interruption",
          "Storm",
          ["Hail / Snow", "Lightning", "Storm / Wind", "Water / Flood"]
        ],
        ["Business Interruption", "Subsidence & Landslip", ["Extended", "Limited"]],
        //Contract Works
        [
          "Contract Works",
          "Accidental Damage",
          ["Accidental Damage General", "In Transit", "Underground Systems"]
        ],
        ["Contract Works", "Earthquake", ["Mining Operations", "Natural Phenomenon"]],
        ["Contract Works", "Explosion", ["Explosion"]],
        [
          "Contract Works",
          "Fire",
          [
            "Electrical Fault",
            "Explosion",
            "External source",
            "Fire General",
            "Mechanical Fault",
            "Poor Housekeeping / Lack of Care",
            "Source off Premises",
            "Spontaneous Combustion",
            "Subterranean"
          ]
        ],
        ["Contract Works", "Malicious Damage", ["Malicious Damage", "Malicious Damage With Theft"]],
        ["Contract Works", "Riot & Strike", ["NASRIA", "Outside RSA / NAM", "SASRIA"]],
        ["Contract Works", "Storm", ["Hail / Snow", "Lightning", "Storm / Wind", "Water / Flood"]],
        ["Contract Works", "Subsidence & Landslip", ["Extended", "Limited"]],
        //Electronic Equipment
        [
          "Electronic Equipment",
          "Accidental Damage",
          [
            "Accidental Damage General",
            "In Transit",
            "Impact by Aircraft / Aerial Devices",
            "Impact by Animals / Trees / Vehicles",
            "Power Surge"
          ]
        ],
        ["Electronic Equipment", "Earthquake", ["Mining Operations", "Natural Phenomenon"]],
        ["Electronic Equipment", "Explosion", ["Explosion"]],
        [
          "Electronic Equipment",
          "Fire",
          [
            "Electrical Fault",
            "Explosion",
            "External source",
            "Fire General",
            "Mechanical Fault",
            "Poor Housekeeping / Lack of Care",
            "Source off Premises",
            "Spontaneous Combustion",
            "Subterranean"
          ]
        ],
        [
          "Electronic Equipment",
          "Leakage",
          [
            "Chemicals / Oils / Fumes",
            "Water - from Fire Extinguishing Installations",
            "Water - from Pipes"
          ]
        ],
        [
          "Electronic Equipment",
          "Malicious Damage",
          ["Malicious Damage", "Malicious Damage With Theft"]
        ],
        ["Electronic Equipment", "Riot & Strike", ["NASRIA", "Outside RSA / NAM", "SASRIA"]],
        [
          "Electronic Equipment",
          "Storm",
          ["Hail / Snow", "Lightning", "Storm / Wind", "Water / Flood"]
        ],
        ["Electronic Equipment", "Subsidence & Landslip", ["Extended", "Limited"]],
        //Employers Liability
        [
          "Employers Liability",
          "Death",
          [
            "Accidental",
            "Building / Premises",
            "Assault",
            "Machinery / Equipment",
            "Road Accident",
            "Toxic Processes",
            "Unhygienic Conditions"
          ]
        ],
        [
          "Employers Liability",
          "Illness",
          [
            "Accidental",
            "Building / Premises",
            "Assault",
            "Machinery / Equipment",
            "Road Accident",
            "Toxic Processes",
            "Unhygienic Conditions"
          ]
        ],
        [
          "Employers Liability",
          "Injury",
          [
            "Accidental",
            "Building / Premises",
            "Assault",
            "Machinery / Equipment",
            "Road Accident",
            "Toxic Processes",
            "Unhygienic Conditions"
          ]
        ],
        //Fidelity Guarantee
        ["Fidelity Guarantee", "Theft", ["Employee Fidelity"]],
        //Fire
        [
          "Fire",
          "Accidental Damage",
          [
            "Impact by Aircraft / Aerial Devices",
            "Impact by Animals / Trees / Vehicles",
            "Power Surge",
            "Public Supply Connections",
            "Wild Animals Damage"
          ]
        ],
        ["Fire", "Earthquake", ["Mining Operations", "Natural Phenomenon"]],
        ["Fire", "Explosion", ["Explosion"]],
        [
          "Fire",
          "Fire",
          [
            "Electrical Fault",
            "Explosion",
            "External source",
            "Fire General",
            "Mechanical Fault",
            "Poor Housekeeping / Lack of Care",
            "Source off Premises",
            "Spontaneous Combustion",
            "Subterranean"
          ]
        ],
        ["Fire", "Geyser", ["Repair", "Replace", "With Resultant Water Damage"]],
        ["Fire", "Leakage", ["Water - from Fire Extinguishing Installations"]],
        ["Fire", "Malicious Damage", ["Malicious Damage", "Malicious Damage With Theft"]],
        ["Fire", "Riot & Strike", ["NASRIA", "Outside RSA / NAM", "SASRIA"]],
        ["Fire", "Storm", ["Hail / Snow", "Lightning", "Storm / Wind", "Water / Flood"]],
        ["Fire", "Subsidence & Landslip", ["Extended", "Limited"]],

        //Glass
        ["Glass", "Accidental Damage", ["Accidental Damage General", "Glass Buildings"]],
        ["Glass", "Malicious Damage", ["Malicious Damage", "Malicious Damage With Theft"]],
        ["Glass", "Riot & Strike", ["NASRIA", "Outside RSA / NAM", "SASRIA"]],
        //Goods In Transit
        [
          "Goods In Transit",
          "Accidental Damage",
          [
            "Accidental Damage General",
            "GIT Collission / Overturning / Derailment",
            "GIT Falling off conveyance"
          ]
        ],
        ["Goods In Transit", "Earthquake", ["Mining Operations", "Natural Phenomenon"]],
        ["Goods In Transit", "Explosion", ["Explosion"]],
        [
          "Goods In Transit",
          "Fire",
          [
            "Electrical Fault",
            "Explosion",
            "External source",
            "Fire General",
            "Mechanical Fault",
            "Poor Housekeeping / Lack of Care",
            "Source off Premises",
            "Spontaneous Combustion",
            "Subterranean"
          ]
        ],
        [
          "Goods In Transit",
          "Malicious Damage",
          ["Malicious Damage", "Malicious Damage With Theft"]
        ],
        ["Goods In Transit", "Riot & Strike", ["NASRIA", "Outside RSA / NAM", "SASRIA"]],
        [
          "Goods In Transit",
          "Storm",
          ["Hail / Snow", "Lightning", "Storm / Wind", "Water / Flood"]
        ],
        ["Goods In Transit", "Subsidence & Landslip", ["Extended", "Limited"]],
        //Group Personal Accident
        [
          "Group Personal Accident",
          "Death",
          [
            "Accidental",
            "Building / Premises",
            "Assault",
            "Machinery / Equipment",
            "Passive War",
            "Road Accident",
            "Toxic Processes",
            "Unhygienic Conditions"
          ]
        ],
        [
          "Group Personal Accident",
          "Illness",
          [
            "Accidental",
            "Building / Premises",
            "Assault",
            "Machinery / Equipment",
            "Road Accident",
            "Passive War",
            "Toxic Processes",
            "Unhygienic Conditions"
          ]
        ],
        [
          "Group Personal Accident",
          "Injury",
          [
            "Accidental",
            "Building / Premises",
            "Assault",
            "Machinery / Equipment",
            "Passive War",
            "Road Accident",
            "Toxic Processes",
            "Unhygienic Conditions"
          ]
        ],
        ["Group Personal Accident", "Medical Expenses", ["Accidental", "Assault", "Road Accident"]],
        //Homeowners
        [
          "Homeowners",
          "Accidental Damage",
          [
            "Accidental Damage General",
            "Aerial Fittings",
            "Domestic Machinery",
            "Glass Buildings",
            "Glass Sanitaryware",
            "Impact by Aircraft / Aerial Devices",
            "Impact by Animals / Trees / Vehicles",
            "Public Supply Connections",
            "Tree Removal Costs",
            "Wild Animals Damage"
          ]
        ],
        ["Homeowners", "Earthquake", ["Mining Operations", "Natural Phenomenon"]],
        ["Homeowners", "Explosion", ["Explosion"]],
        [
          "Homeowners",
          "Fire",
          [
            "Electrical Fault",
            "Explosion",
            "External source",
            "Fire General",
            "Mechanical Fault",
            "Poor Housekeeping / Lack of Care",
            "Source off Premises",
            "Spontaneous Combustion",
            "Subterranean"
          ]
        ],
        [
          "Homeowners",
          "Leakage",
          [
            "Chemicals / Oils / Fumes",
            "Water - from Fire Extinguishing Installations",
            "Water - from Pipes",
            "Water - Leakage Costs"
          ]
        ],
        ["Homeowners", "Locks & Keys", ["Locks & Keys"]],
        ["Homeowners", "Malicious Damage", ["Malicious Damage", "Malicious Damage With Theft"]],
        ["Homeowners", "Riot & Strike", ["NASRIA", "Outside RSA / NAM", "SASRIA"]],
        ["Homeowners", "Storm", ["Hail / Snow", "Lightning", "Storm / Wind", "Water / Flood"]],
        ["Homeowners", "Subsidence & Landslip", ["Extended", "Limited"]],
        [
          "Homeowners",
          "Theft",
          [
            "From Premises",
            "From Unattended Vehicle",
            "General",
            "Hijack",
            "Temporarily Removed from Building"
          ]
        ],
        //House Holders
        [
          "House Holders",
          "Accidental Damage",
          [
            "Accidental Damage General",
            "Aerial Fittings",
            "Audio Visual Equipment",
            "Deterioration of Stock",
            "Domestic Machinery",
            "Glass Furniture",
            "Impact by Aircraft / Aerial Devices",
            "Impact by Animals / Trees / Vehicles",
            "In Transit",
            "Power Surge"
          ]
        ],
        ["House Holders", "Earthquake", ["Mining Operations", "Natural Phenomenon"]],
        ["House Holders", "Explosion", ["Explosion"]],
        ["House Holders", "Death", ["Accidental"]],
        ["House Holders", "Injury", ["Accidental"]],
        [
          "House Holders",
          "Fire",
          [
            "Electrical Fault",
            "Explosion",
            "External source",
            "Fire General",
            "Mechanical Fault",
            "Poor Housekeeping / Lack of Care",
            "Source off Premises",
            "Spontaneous Combustion",
            "Subterranean"
          ]
        ],
        [
          "House Holders",
          "Leakage",
          [
            "Chemicals / Oils / Fumes",
            "Water - from Fire Extinguishing Installations",
            "Water - from Pipes",
            "Water - Leakage Costs"
          ]
        ],
        ["House Holders", "Geyser", ["Repair", "Replace", "With Resultant Water Damage"]],
        ["House Holders", "Locks & Keys", ["Locks & Keys"]],
        ["House Holders", "Malicious Damage", ["Malicious Damage", "Malicious Damage With Theft"]],
        [
          "House Holders",
          "Machinery Breakdown",
          ["Electrical Breakdown", "Electronical Breakdown", "Mechanical Breakdown"]
        ],
        ["House Holders", "Medical Expenses", ["Accidental"]],
        ["House Holders", "Riot & Strike", ["NASRIA", "Outside RSA / NAM", "SASRIA"]],
        ["House Holders", "Storm", ["Hail / Snow", "Lightning", "Storm / Wind", "Water / Flood"]],
        ["House Holders", "Subsidence & Landslip", ["Extended", "Limited"]],
        ["House Holders", "Theft", ["From Unattended Vehicle"]],

        //Machinery Breakdown
        [
          "Machinery Breakdown",
          "Accidental Damage",
          ["Accidental Damage General", "Deterioration of Stock", "Power Surge"]
        ],
        ["Machinery Breakdown", "Explosion", ["Explosion"]],
        ["Machinery Breakdown", "Fire", ["Electrical Fault", "Explosion"]],
        [
          "Machinery Breakdown",
          "Machinery Breakdown",
          [
            "Centrifugal forces",
            "Electrical Breakdown",
            "Electronical Breakdown",
            "Excessive Electrical Pressure",
            "Faulty Casting, Construction, Erection",
            "Faulty Design",
            "Faulty Material",
            "Faulty Workmanship, Lack of Skill, Carelessness",
            "Loosening of Parts",
            "Mechanical Breakdown",
            "Obstruction or entry of foreign bodies",
            "Short Circuit",
            "Shortage of Water in Boilers",
            "Vibration, mal-adjustment, mal-alignment"
          ]
        ],

        //Money
        ["Money", "Injury", ["Assault"]],
        ["Money", "Medical Expenses", ["Assault"]],
        ["Money", "Locks & Keys", ["Locks & Keys"]],
        [
          "Money",
          "Money Loss",
          [
            "At Insured Premises During Business Hours",
            "At Insured Premises Outside Business Hours",
            "Cheque Fraud",
            "In the Custody of a Collector / Roundsman",
            "In the Custody of a Petrol Attendant",
            "In Transit By Employee(s)",
            "In Transit By Security Firm",
            "Official on Business Trip",
            "Officials Residence"
          ]
        ],
        ["Money", "Riot & Strike", ["NASRIA", "Outside RSA / NAM", "SASRIA"]],
        ["Money", "Death", ["Assault"]],
        //Office Contents
        [
          "Office Contents",
          "Accidental Damage",
          [
            "Accidental Damage General",
            "Documents",
            "Glass Furniture",
            "Impact by Aircraft / Aerial Devices",
            "Impact by Animals / Trees / Vehicles",
            "Power Surge"
          ]
        ],
        ["Office Contents", "Earthquake", ["Mining Operations", "Natural Phenomenon"]],
        ["Office Contents", "Explosion", ["Explosion"]],
        [
          "Office Contents",
          "Fire",
          [
            "Electrical Fault",
            "Explosion",
            "External source",
            "Fire General",
            "Mechanical Fault",
            "Poor Housekeeping / Lack of Care",
            "Source off Premises",
            "Spontaneous Combustion",
            "Subterranean"
          ]
        ],
        ["Office Contents", "Geyser", ["Repair", "Replace", "With Resultant Water Damage"]],
        [
          "Office Contents",
          "Leakage",
          [
            "Chemicals / Oils / Fumes",
            "Water - from Fire Extinguishing Installations",
            "Water - from Pipes"
          ]
        ],
        ["Office Contents", "Locks & Keys", ["Locks & Keys"]],
        [
          "Office Contents",
          "Malicious Damage",
          ["Malicious Damage", "Malicious Damage With Theft"]
        ],
        ["Office Contents", "Riot & Strike", ["NASRIA", "Outside RSA / NAM", "SASRIA"]],
        ["Office Contents", "Storm", ["Hail / Snow", "Lightning", "Storm / Wind", "Water / Flood"]],
        ["Office Contents", "Subsidence & Landslip", ["Extended", "Limited"]],
        //Personal Liability
        [
          "Personal Liability",
          "CUP/PLIP",
          [
            "General - Additional Risks",
            "General - Difference in Conditions",
            "General - Excess Layer",
            "Motor - Additional Risks",
            "Motor - Difference in Conditions",
            "Motor - Excess Layer",
            "Property Owners - Additional Risks",
            "Property Owners - Difference in Conditions",
            "Property Owners - Excess Layer",
            "Tenants - Additional Risks",
            "Tenants - Difference in Conditions",
            "Tenants - Excess Layer",
            "Watercraft - Additional Risks",
            "Watercraft - Difference in Conditions",
            "Watercraft - Excess Layer"
          ]
        ],
        ["Personal Liability", "Death", ["Accidental"]],
        ["Personal Liability", "Injury", ["Accidental"]],
        ["Personal Liability", "Illness", ["Accidental"]],
        [
          "Personal Liability",
          "Liability",
          [
            "Bowls Full-House",
            "First Aid",
            "General",
            "Golfers Hole-in-One",
            "Lawn Mowers & Gardening Tools",
            "Neighbourhood Watch",
            "Security Firm",
            "Tenants Liability"
          ]
        ],

        //Plant
        [
          "Plant",
          "Accidental Damage",
          [
            "Accidental Damage General",
            "GIT Collission / Overturning / Derailment",
            "GIT Falling off conveyance",
            "Impact by Aircraft / Aerial Devices",
            "Impact by Animals / Trees / Vehicles",
            "In Transit"
          ]
        ],
        ["Plant", "Earthquake", ["Mining Operations", "Natural Phenomenon"]],
        ["Plant", "Explosion", ["Explosion"]],
        [
          "Plant",
          "Fire",
          [
            "Electrical Fault",
            "Explosion",
            "External source",
            "Fire General",
            "Mechanical Fault",
            "Poor Housekeeping / Lack of Care",
            "Source off Premises",
            "Spontaneous Combustion",
            "Subterranean"
          ]
        ],
        ["Plant", "Liability", ["General"]],
        ["Plant", "Malicious Damage", ["Malicious Damage", "Malicious Damage With Theft"]],
        ["Plant", "Riot & Strike", ["NASRIA", "Outside RSA / NAM", "SASRIA"]],
        ["Plant", "Storm", ["Hail / Snow", "Lightning", "Storm / Wind", "Water / Flood"]],

        //Public Liability
        [
          "Public Liability",
          "CUP/PLIP",
          [
            "General - Additional Risks",
            "General - Difference in Conditions",
            "General - Excess Layer",
            "Motor - Additional Risks",
            "Motor - Difference in Conditions",
            "Motor - Excess Layer",
            "Property Owners - Additional Risks",
            "Property Owners - Difference in Conditions",
            "Property Owners - Excess Layer",
            "Tenants - Additional Risks",
            "Tenants - Difference in Conditions",
            "Tenants - Excess Layer",
            "Watercraft - Additional Risks",
            "Watercraft - Difference in Conditions",
            "Watercraft - Excess Layer"
          ]
        ],
        ["Public Liability", "Death", ["Accidental"]],
        ["Public Liability", "Injury", ["Accidental"]],
        ["Public Liability", "Illness", ["Accidental"]],
        [
          "Public Liability",
          "Liability",
          [
            "Animals - Auctions",
            "Animals - Driven / Escaped / Stray",
            "Animals - Grazing (not Insureds)",
            "Animals - Vehicles Drawn by",
            "Beauty Salon",
            "Breaking / Overflowing of Walls / Dams",
            "Car Parks",
            "Car Wash & Valet",
            "Chemist Prescription",
            "Crop Spraying",
            "Defective Workmanship",
            "Fire Spread - Other Than Sugar-Cane and Plantations",
            "Fire Spread - Sugar-Cane and Plantations",
            "First Aid",
            "Forecourt Service",
            "Game Hunting & Viewing",
            "General",
            "Government & Other Authorities Agreements",
            "Gratuitous Advice",
            "Hairdressers",
            "Hotel - Goods In Custody",
            "Lawn Mowers & Gardening Tools",
            "Neighbourhood Watch",
            "Products - Exported to E.E.C. Countries",
            "Products - Exported to Other Countries",
            "Products - Exported to U.S.A / Canada",
            "Products - General",
            "Products - Guarantee",
            "Products - Inefficacy",
            "Products - Recall",
            "Professional",
            "Schools",
            "Security Firm",
            "Statutory Legal Defence Costs",
            "Tenants Liability",
            "Tool of Trade",
            "Unattached Trailers",
            "Visitors & Employees Property",
            "Warehousemens Liability",
            "Work Away from Insured Premises",
            "Wrongful Act - Arrest & Defamation",
            "Wrongful Act - Directors",
            "Wrongful Act - Trustee"
          ]
        ],

        //Special Risks
        [
          "Special Risks",
          "Accidental Damage",
          [
            "Accidental Damage General",
            "Glass Buildings",
            "Impact by Aircraft / Aerial Devices",
            "Impact by Animals / Trees / Vehicles",
            "Power Surge",
            "Public Supply Connections",
            "Wild Animals Damage"
          ]
        ],
        ["Special Risks", "Claims Preparation Costs", ["Claims Preparation Costs"]],
        [
          "Special Risks",
          "CUP/PLIP",
          [
            "General - Additional Risks",
            "General - Difference in Conditions",
            "General - Excess Layer",
            "Motor - Additional Risks",
            "Motor - Difference in Conditions",
            "Motor - Excess Layer",
            "Property Owners - Additional Risks",
            "Property Owners - Difference in Conditions",
            "Property Owners - Excess Layer",
            "Tenants - Additional Risks",
            "Tenants - Difference in Conditions",
            "Tenants - Excess Layer",
            "Watercraft - Additional Risks",
            "Watercraft - Difference in Conditions",
            "Watercraft - Excess Layer"
          ]
        ],
        ["Special Risks", "Director & Officers Indemnity", ["Director & Officers Indemnity"]],
        ["Special Risks", "Earthquake", ["Mining Operations", "Natural Phenomenon"]],
        ["Special Risks", "Explosion", ["Explosion"]],
        ["Special Risks", "Geyser", ["Repair", "Replace", "With Resultant Water Damage"]],
        ["Special Risks", "Landscaped Gardens", ["Landscaped Gardens"]],
        [
          "Special Risks",
          "Leakage",
          [
            "Chemicals / Oils / Fumes",
            "Water - from Fire Extinguishing Installations",
            "Water - from Pipes",
            "Water - Leakage Costs"
          ]
        ],
        ["Special Risks", "Locks & Keys", ["Locks & Keys"]],
        ["Special Risks", "Malicious Damage", ["Malicious Damage", "Malicious Damage With Theft"]],
        ["Special Risks", "Riot & Strike", ["NASRIA", "Outside RSA / NAM", "SASRIA"]],
        ["Special Risks", "Storm", ["Hail / Snow", "Lightning", "Storm / Wind", "Water / Flood"]],
        ["Special Risks", "Subsidence & Landslip", ["Extended", "Limited"]],
        [
          "Special Risks",
          "Power Surge Cover to Non Electronic Equipment",
          ["Power Surge Cover to Non Electronic Equipment"]
        ],
        ["Special Risks", "Security Services", ["Security Services"]],
        ["Special Risks", "Excess buy back", ["Excess buy back"]],
        [
          "Special Risks",
          "Theft",
          [
            "From Premises",
            "From Unattended Vehicle",
            "General",
            "Hijack",
            "Temporarily Removed from Building"
          ]
        ],
        [
          "Special Risks",
          "Vehicle - Accident",
          [
            "Animals / Trees",
            "Falling Objects",
            "Fixed Property",
            "Other vehicle(s)",
            "Parked - damaged by TP",
            "Pedestrian / Cyclist",
            "Potholes / Inappropriate road surface",
            "Single Vehicle Accident"
          ]
        ],
        ["Special Risks", "Vehicle - Glass", ["Repair", "Replace"]],
        [
          "Special Risks",
          "Vehicle - Roadside Assistance",
          [
            "Electrical Breakdown",
            "Flat Battery / Tyre",
            "Keys locked in vehicle",
            "Mechanical Breakdown"
          ]
        ],
        [
          "Special Risks",
          "Vehicle - Theft",
          [
            "Equipment & Damage to vehicle",
            "Equipment & No damage to vehicle",
            "Vehicle - Abscondence",
            "Vehicle - Hijack",
            "Vehicle - Theft"
          ]
        ],
        ["Special Risks", "Waiver of basic excess only", ["Waiver of basic excess only"]],
        ["Special Risks", "Wear and Tear on Internal Pipes", ["Wear and Tear on Internal Pipes"]],
        [
          "Special Risks",
          "Fire",
          [
            "Electrical Fault",
            "Explosion",
            "External source",
            "Fire General",
            "Mechanical Fault",
            "Poor Housekeeping / Lack of Care",
            "Source off Premises",
            "Spontaneous Combustion",
            "Subterranean"
          ]
        ],
        //Stated Benefits
        [
          "Stated Benefits",
          "Death",
          [
            "Accidental",
            "Building / Premises",
            "Assault",
            "Machinery / Equipment",
            "Road Accident",
            "Toxic Processes",
            "Unhygienic Conditions",
            "Passive War"
          ]
        ],
        [
          "Stated Benefits",
          "Illness",
          [
            "Accidental",
            "Building / Premises",
            "Assault",
            "Machinery / Equipment",
            "Road Accident",
            "Toxic Processes",
            "Unhygienic Conditions",
            "Passive War"
          ]
        ],
        [
          "Stated Benefits",
          "Injury",
          [
            "Accidental",
            "Building / Premises",
            "Assault",
            "Machinery / Equipment",
            "Road Accident",
            "Toxic Processes",
            "Unhygienic Conditions"
          ]
        ],
        ["Stated Benefits", "Medical Expenses", ["Accidental", "Assault", "Road Accident"]],
        //Theft
        ["Theft", "Locks & Keys", ["Locks & Keys"]],

        //Watercraft
        [
          "Watercraft",
          "Accidental Damage",
          [
            "Accidental Damage General",
            "GIT Collission / Overturning / Derailment",
            "GIT Falling off conveyance",
            "Impact by Aircraft / Aerial Devices",
            "In Transit"
          ]
        ],
        ["Watercraft", "Earthquake", ["Mining Operations", "Natural Phenomenon"]],
        ["Watercraft", "Explosion", ["Explosion"]],
        [
          "Watercraft",
          "Fire",
          [
            "Electrical Fault",
            "Explosion",
            "External source",
            "Fire General",
            "Mechanical Fault",
            "Poor Housekeeping / Lack of Care",
            "Source off Premises",
            "Spontaneous Combustion",
            "Subterranean"
          ]
        ],
        ["Watercraft", "Liability", ["General"]],
        ["Watercraft", "Malicious Damage", ["Malicious Damage", "Malicious Damage With Theft"]],
        [
          "Watercraft",
          "Marine",
          [
            "Accidental Damage",
            "Capsizing",
            "Force of Nature",
            "Impact Damage",
            "Machinery Damage",
            "Malicious Damage",
            "Outboards Overboard",
            "Pirating",
            "Repairs Negligence",
            "Shortages / Non delivery of Cargo",
            "Sinking",
            "Stranding",
            "Submerged Object Struck",
            "War"
          ]
        ],
        ["Watercraft", "Medical Expenses", ["Accidental"]],
        ["Watercraft", "Riot & Strike", ["NASRIA", "Outside RSA / NAM", "SASRIA"]],
        ["Watercraft", "Storm", ["Hail / Snow", "Lightning", "Storm / Wind", "Water / Flood"]]
      ],
      schemaIndex: 2,
      action: () => removeSchemasByTitle("Police")
    },
    //Loss Caused by Other Parties -form 4
    {
      causes: [
        //"Accidental Damage"
        [
          "Accidental Damage",
          "Accidental Damage",
          ["Aerial Fittings", "Lost / Misplaced", "Power Surge"]
        ],
        [
          "Accidental Damage",
          "Accidental Damage - Wine",
          [
            "Breakage - Bottled Wine",
            "Breakage - Bottles",
            "Breakage - Tanks",
            "Contamination",
            "Fermentation",
            "Latent Defect",
            "Leakage - Stock",
            "Leakage - Surrounding Property - Not Wine",
            "Leakage - Surrounding Property - Wine",
            "Loading / Off Loading"
          ]
        ],
        //Accounts Receivable
        ["Accounts Receivable", "Earthquake", ["Mining Operations", "Natural Phenomenon"]],
        ["Accounts Receivable", "Explosion", ["Explosion"]],
        [
          "Accounts Receivable",
          "Fire",
          [
            "Electrical Fault",
            "Explosion",
            "External source",
            "Fire General",
            "Mechanical Fault",
            "Poor Housekeeping / Lack of Care",
            "Source off Premises",
            "Spontaneous Combustion",
            "Subterranean"
          ]
        ],
        [
          "Accounts Receivable",
          "Malicious Damage",
          ["Malicious Damage", "Malicious Damage With Theft"]
        ],
        ["Accounts Receivable", "Riot & Strike", ["NASRIA", "Outside RSA / NAM", "SASRIA"]],
        [
          "Accounts Receivable",
          "Storm",
          ["Hail / Snow", "Lightning", "Storm / Wind", "Water / Flood"]
        ],
        ["Accounts Receivable", "Subsidence & Landslip", ["Extended", "Limited"]],
        ["Accounts Receivable", "Theft", ["General"]],
        //all risk
        ["All Risk", "Accidental Damage", ["Accidental Damage General", "Lost / Misplaced"]],
        ["All Risk", "Earthquake", ["Mining Operations", "Natural Phenomenon"]],
        ["All Risk", "Explosion", ["Explosion"]],
        [
          "All Risk",
          "Fire",
          [
            "Electrical Fault",
            "Explosion",
            "External source",
            "Fire General",
            "Mechanical Fault",
            "Poor Housekeeping / Lack of Care",
            "Source off Premises",
            "Spontaneous Combustion",
            "Subterranean"
          ]
        ],
        [
          "All Risk",
          "Leakage",
          [
            "Chemicals / Oils / Fumes",
            "Water - from Fire Extinguishing Installations",
            "Water - from Pipes"
          ]
        ],
        ["All Risk", "Malicious Damage", ["Malicious Damage", "Malicious Damage With Theft"]],
        ["All Risk", "Riot & Strike", ["NASRIA", "Outside RSA / NAM", "SASRIA"]],
        ["All Risk", "Storm", ["Hail / Snow", "Lightning", "Storm / Wind", "Water / Flood"]],
        ["All Risk", "Subsidence & Landslip", ["Extended", "Limited"]],
        [
          "All Risk",
          "Theft",
          [
            "From Premises",
            "From Unattended Vehicle",
            "General",
            "Hijack",
            "Temporarily Removed from Building",
            "With Force",
            "Without Force"
          ]
        ],
        //body corporate
        [
          "Body Corporate",
          "Accidental Damage",
          [
            "Aerial Fittings",
            "Accidental Damage General",
            "Domestic Machinery",
            "Glass Buildings",
            "Glass Sanitaryware",
            "Impact by Aircraft / Aerial Devices",
            "Impact by Animals / Trees / Vehicles",
            "Power Surge",
            "Public Supply Connections",
            "Tree Removal Costs",
            "Wild Animals Damage"
          ]
        ],
        ["Body Corporate", "Death", ["Accidental", "Building / Premises"]],
        ["Body Corporate", "Earthquake", ["Mining Operations", "Natural Phenomenon"]],
        ["Body Corporate", "Explosion", ["Explosion"]],
        [
          "Body Corporate",
          "Fire",
          [
            "Electrical Fault",
            "Explosion",
            "External source",
            "Fire General",
            "Mechanical Fault",
            "Poor Housekeeping / Lack of Care",
            "Source off Premises",
            "Spontaneous Combustion",
            "Subterranean"
          ]
        ],
        [
          "Body Corporate",
          "Geyser",
          ["Repair", "Replacement", "With Resultant Water Damage", "Replace"]
        ],
        ["Body Corporate", "Illness", ["Accidental", "Building / Premises"]],
        ["Body Corporate", "Injury", ["Accidental", "Building / Premises"]],
        [
          "Body Corporate",
          "Leakage",
          [
            "Chemicals / Oils / Fumes",
            "Water - from Fire Extinguishing Installations",
            "Water - from Pipes",
            "Water - Leakage Costs"
          ]
        ],
        ["Body Corporate", "Liability", ["General", "Security Firm"]],
        ["Body Corporate", "Locks & Keys", ["Locks & Keys"]],
        ["Body Corporate", "Malicious Damage", ["Malicious Damage", "Malicious Damage With Theft"]],
        ["Body Corporate", "Riot & Strike", ["NASRIA", "Outside RSA / NAM", "SASRIA"]],
        ["Body Corporate", "Storm", ["Hail / Snow", "Lightning", "Storm / Wind", "Water / Flood"]],
        ["Body Corporate", "Subsidence & Landslip", ["Extended", "Limited"]],
        [
          "Body Corporate",
          "Theft",
          ["From Premises", "From Unattended Vehicle", "General", "With Force", "Without Force"]
        ],
        //Buildings Combined
        [
          "Buildings Combined",
          "Accidental Damage",
          [
            "Aerial Fittings",
            "Domestic Machinery",
            "Glass Buildings",
            "Glass Sanitaryware",
            "Power Surge",
            "Public Supply Connections",
            "Tree Removal Costs"
          ]
        ],
        ["Buildings Combined", "Earthquake", ["Mining Operations", "Natural Phenomenon"]],
        ["Buildings Combined", "Explosion", ["Explosion"]],
        [
          "Buildings Combined",
          "Fire",
          [
            "Electrical Fault",
            "Explosion",
            "External source",
            "Fire General",
            "Mechanical Fault",
            "Poor Housekeeping / Lack of Care",
            "Source off Premises",
            "Spontaneous Combustion",
            "Subterranean"
          ]
        ],
        ["Buildings Combined", "Geyser", ["Repair", "Replacement", "With Resultant Water Damage"]],
        ["Buildings Combined", "Illness", ["Accidental", "Building / Premises"]],
        ["Buildings Combined", "Injury", ["Building / Premises"]],
        ["Buildings Combined", "Death", ["Building / Premises"]],
        ["Buildings Combined", "Locks & Keys", ["Locks & Keys"]],
        [
          "Buildings Combined",
          "Malicious Damage",
          ["Malicious Damage", "Malicious Damage With Theft"]
        ],
        ["Buildings Combined", "Riot & Strike", ["NASRIA", "Outside RSA / NAM", "SASRIA"]],
        [
          "Buildings Combined",
          "Storm",
          ["Hail / Snow", "Lightning", "Storm / Wind", "Water / Flood"]
        ],
        ["Buildings Combined", "Subsidence & Landslip", ["Extended", "Limited"]],
        ["Buildings Combined", "Theft", ["From Premises", "General"]],

        //Business All Risks
        ["Business All Risks", "Accidental Damage", ["Lost / Misplaced"]],
        ["Business All Risks", "Earthquake", ["Mining Operations", "Natural Phenomenon"]],
        ["Business All Risks", "Explosion", ["Explosion"]],
        [
          "Business All Risks",
          "Fire",
          [
            "Electrical Fault",
            "Explosion",
            "External source",
            "Fire General",
            "Mechanical Fault",
            "Poor Housekeeping / Lack of Care",
            "Source off Premises",
            "Spontaneous Combustion",
            "Subterranean"
          ]
        ],
        [
          "Business All Risks",
          "Malicious Damage",
          ["Malicious Damage", "Malicious Damage With Theft"]
        ],
        ["Business All Risks", "Riot & Strike", ["NASRIA", "Outside RSA / NAM", "SASRIA"]],
        [
          "Business All Risks",
          "Storm",
          ["Hail / Snow", "Lightning", "Storm / Wind", "Water / Flood"]
        ],
        ["Business All Risks", "Subsidence & Landslip", ["Extended", "Limited"]],
        [
          "Business All Risks",
          "Theft",
          [
            "General",
            "From Premises",
            "From Unattended Vehicle",
            "Hijack",
            "Temporarily Removed from Building"
          ]
        ],

        //Business Interruption

        ["Business Interruption", "Accidental Damage", ["Deterioration of Stock"]],
        ["Business Interruption", "Earthquake", ["Mining Operations", "Natural Phenomenon"]],
        ["Business Interruption", "Explosion", ["Explosion"]],
        [
          "Business Interruption",
          "Fire",
          [
            "Electrical Fault",
            "Explosion",
            "External source",
            "Fire General",
            "Mechanical Fault",
            "Poor Housekeeping / Lack of Care",
            "Source off Premises",
            "Spontaneous Combustion",
            "Subterranean"
          ]
        ],
        [
          "Business Interruption",
          "Malicious Damage",
          ["Malicious Damage", "Malicious Damage With Theft"]
        ],
        ["Business Interruption", "Riot & Strike", ["NASRIA", "Outside RSA / NAM", "SASRIA"]],
        [
          "Business Interruption",
          "Storm",
          ["Hail / Snow", "Lightning", "Storm / Wind", "Water / Flood"]
        ],
        ["Business Interruption", "Subsidence & Landslip", ["Extended", "Limited"]],
        ["Business Interruption", "Theft", ["From Premises", "General"]],

        //Contract Works
        ["Contract Works", "Earthquake", ["Mining Operations", "Natural Phenomenon"]],
        ["Contract Works", "Explosion", ["Explosion"]],
        ["Contract Works", "Malicious Damage", ["Malicious Damage", "Malicious Damage With Theft"]],
        ["Contract Works", "Riot & Strike", ["NASRIA", "Outside RSA / NAM", "SASRIA"]],
        ["Contract Works", "Storm", ["Hail / Snow", "Lightning", "Storm / Wind", "Water / Flood"]],
        ["Contract Works", "Subsidence & Landslip", ["Extended", "Limited"]],
        ["Contract Works", "Theft", ["From Premises", "From Unattended Vehicle", "General"]],

        //Electronic Equipment
        ["Electronic Equipment", "Explosion", ["Explosion"]],
        ["Electronic Equipment", "Accidental Damage", ["Power Surge"]],
        ["Electronic Equipment", "Earthquake", ["Mining Operations", "Natural Phenomenon"]],
        [
          "Electronic Equipment",
          "Malicious Damage",
          ["Malicious Damage", "Malicious Damage With Theft"]
        ],
        ["Electronic Equipment", "Riot & Strike", ["NASRIA", "Outside RSA / NAM", "SASRIA"]],
        [
          "Electronic Equipment",
          "Storm",
          ["Hail / Snow", "Lightning", "Storm / Wind", "Water / Flood"]
        ],
        ["Electronic Equipment", "Subsidence & Landslip", ["Extended", "Limited"]],
        [
          "Electronic Equipment",
          "Theft",
          [
            "From Premises",
            "From Unattended Vehicle",
            "General",
            "Hijack",
            "Temporarily Removed from Building"
          ]
        ],

        //Employers Liability
        [
          "Employers Liability",
          "Death",
          [
            "Accidental",
            "Building / Premises",
            "Assault",
            "Machinery / Equipment",
            "Road Accident",
            "Toxic Processes",
            "Unhygienic Conditions"
          ]
        ],
        [
          "Employers Liability",
          "Illness",
          [
            "Accidental",
            "Building / Premises",
            "Assault",
            "Machinery / Equipment",
            "Road Accident",
            "Toxic Processes",
            "Unhygienic Conditions"
          ]
        ],
        [
          "Employers Liability",
          "Injury",
          [
            "Accidental",
            "Building / Premises",
            "Assault",
            "Machinery / Equipment",
            "Road Accident",
            "Toxic Processes",
            "Unhygienic Conditions"
          ]
        ],
        //Fidelity Guarantee
        ["Fidelity Guarantee", "Theft", ["Employee Fidelity"]],
        //Fire
        [
          "Fire",
          "Accidental Damage",
          [
            "Impact by Aircraft / Aerial Devices",
            "Impact by Animals / Trees / Vehicles",
            "Power Surge",
            "Public Supply Connections",
            "Wild Animals Damage"
          ]
        ],
        ["Fire", "Earthquake", ["Mining Operations", "Natural Phenomenon"]],
        ["Fire", "Explosion", ["Explosion"]],
        [
          "Fire",
          "Fire",
          [
            "Electrical Fault",
            "Explosion",
            "External source",
            "Fire General",
            "Mechanical Fault",
            "Poor Housekeeping / Lack of Care",
            "Source off Premises",
            "Spontaneous Combustion",
            "Subterranean"
          ]
        ],
        ["Fire", "Geyser", ["Repair", "Replace", "With Resultant Water Damage"]],
        ["Fire", "Leakage", ["Water - from Fire Extinguishing Installations"]],
        ["Fire", "Malicious Damage", ["Malicious Damage", "Malicious Damage With Theft"]],
        ["Fire", "Riot & Strike", ["NASRIA", "Outside RSA / NAM", "SASRIA"]],
        ["Fire", "Storm", ["Hail / Snow", "Lightning", "Storm / Wind", "Water / Flood"]],
        ["Fire", "Subsidence & Landslip", ["Extended", "Limited"]],

        //Glass
        ["Glass", "Accidental Damage", ["Glass Buildings"]],
        ["Glass", "Malicious Damage", ["Malicious Damage", "Malicious Damage With Theft"]],
        ["Glass", "Riot & Strike", ["NASRIA", "Outside RSA / NAM", "SASRIA"]],
        //Goods In Transit

        ["Goods In Transit", "Earthquake", ["Mining Operations", "Natural Phenomenon"]],
        ["Goods In Transit", "Explosion", ["Explosion"]],
        [
          "Goods In Transit",
          "Fire",
          [
            "Electrical Fault",
            "Explosion",
            "External source",
            "Fire General",
            "Mechanical Fault",
            "Poor Housekeeping / Lack of Care",
            "Source off Premises",
            "Spontaneous Combustion",
            "Subterranean"
          ]
        ],
        [
          "Goods In Transit",
          "Malicious Damage",
          ["Malicious Damage", "Malicious Damage With Theft"]
        ],
        ["Goods In Transit", "Riot & Strike", ["NASRIA", "Outside RSA / NAM", "SASRIA"]],
        [
          "Goods In Transit",
          "Storm",
          ["Hail / Snow", "Lightning", "Storm / Wind", "Water / Flood"]
        ],
        ["Goods In Transit", "Subsidence & Landslip", ["Extended", "Limited"]],
        ["Goods In Transit", "Theft", ["From Unattended Vehicle", "General", "Hijack"]],
        //Group Personal Accident
        [
          "Group Personal Accident",
          "Accidental Damage",
          [
            "Aerial Fittings",
            "Domestic Machinery",
            "Glass Buildings",
            "Glass Sanitaryware",
            "Public Supply Connections",
            "Tree Removal Costs"
          ]
        ],
        [
          "Group Personal Accident",
          "Death",
          [
            "Accidental",
            "Building / Premises",
            "Assault",
            "Machinery / Equipment",
            "Passive War",
            "Road Accident",
            "Toxic Processes",
            "Unhygienic Conditions"
          ]
        ],
        [
          "Group Personal Accident",
          "Illness",
          [
            "Accidental",
            "Building / Premises",
            "Assault",
            "Machinery / Equipment",
            "Road Accident",
            "Passive War",
            "Toxic Processes",
            "Unhygienic Conditions"
          ]
        ],
        [
          "Group Personal Accident",
          "Injury",
          [
            "Accidental",
            "Building / Premises",
            "Assault",
            "Machinery / Equipment",
            "Passive War",
            "Road Accident",
            "Toxic Processes",
            "Unhygienic Conditions"
          ]
        ],
        ["Group Personal Accident", "Medical Expenses", ["Accidental", "Assault", "Road Accident"]],
        //Homeowners
        [
          "Homeowners",
          "Accidental Damage",
          [
            "Aerial Fittings",
            "Domestic Machinery",
            "Glass Buildings",
            "Glass Sanitaryware",
            "Public Supply Connections",
            "Tree Removal Costs"
          ]
        ],
        ["Homeowners", "Earthquake", ["Mining Operations", "Natural Phenomenon"]],
        ["Homeowners", "Explosion", ["Explosion"]],
        [
          "Homeowners",
          "Fire",
          [
            "Electrical Fault",
            "Explosion",
            "External source",
            "Fire General",
            "Mechanical Fault",
            "Poor Housekeeping / Lack of Care",
            "Source off Premises",
            "Spontaneous Combustion",
            "Subterranean"
          ]
        ],
        ["Homeowners", "Locks & Keys", ["Locks & Keys"]],
        ["Homeowners", "Malicious Damage", ["Malicious Damage", "Malicious Damage With Theft"]],
        ["Homeowners", "Riot & Strike", ["NASRIA", "Outside RSA / NAM", "SASRIA"]],
        ["Homeowners", "Storm", ["Hail / Snow", "Lightning", "Storm / Wind", "Water / Flood"]],
        ["Homeowners", "Subsidence & Landslip", ["Extended", "Limited"]],

        //House Holders
        [
          "House Holders",
          "Accidental Damage",
          [
            "Aerial Fittings",
            "Audio Visual Equipment",
            "Deterioration of Stock",
            "Domestic Machinery",
            "Glass Furniture",
            "Power Surge"
          ]
        ],
        ["House Holders", "Earthquake", ["Mining Operations", "Natural Phenomenon"]],
        ["House Holders", "Explosion", ["Explosion"]],
        [
          "House Holders",
          "Fire",
          [
            "Electrical Fault",
            "Explosion",
            "External source",
            "Fire General",
            "Mechanical Fault",
            "Poor Housekeeping / Lack of Care",
            "Source off Premises",
            "Spontaneous Combustion",
            "Subterranean"
          ]
        ],
        ["House Holders", "Geyser", ["Repair", "Replace", "With Resultant Water Damage"]],
        ["House Holders", "Locks & Keys", ["Locks & Keys"]],
        ["House Holders", "Malicious Damage", ["Malicious Damage", "Malicious Damage With Theft"]],
        [
          "House Holders",
          "Machinery Breakdown",
          ["Electrical Breakdown", "Electronical Breakdown", "Mechanical Breakdown"]
        ],

        ["House Holders", "Riot & Strike", ["NASRIA", "Outside RSA / NAM", "SASRIA"]],
        ["House Holders", "Storm", ["Hail / Snow", "Lightning", "Storm / Wind", "Water / Flood"]],
        ["House Holders", "Subsidence & Landslip", ["Extended", "Limited"]],
        [
          "House Holders",
          "Theft",
          [
            "From Unattended Vehicle",
            "From Premises",
            "General",
            "Temporarily Removed from Building"
          ]
        ],

        //Machinery Breakdown
        ["Machinery Breakdown", "Accidental Damage", ["Deterioration of Stock", "Power Surge"]],
        ["Machinery Breakdown", "Explosion", ["Explosion"]],
        ["Machinery Breakdown", "Fire", ["Electrical Fault", "Explosion"]],
        [
          "Machinery Breakdown",
          "Machinery Breakdown",
          [
            "Centrifugal forces",
            "Electrical Breakdown",
            "Electronical Breakdown",
            "Excessive Electrical Pressure",
            "Faulty Casting, Construction, Erection",
            "Faulty Design",
            "Faulty Material",
            "Faulty Workmanship, Lack of Skill, Carelessness",
            "Loosening of Parts",
            "Mechanical Breakdown",
            "Obstruction or entry of foreign bodies",
            "Short Circuit",
            "Shortage of Water in Boilers",
            "Vibration, mal-adjustment, mal-alignment"
          ]
        ],
        //Money
        ["Money", "Injury", ["Assault"]],
        ["Money", "Medical Expenses", ["Assault"]],
        ["Money", "Locks & Keys", ["Locks & Keys"]],
        [
          "Money",
          "Money Loss",
          [
            "At Insured Premises During Business Hours",
            "At Insured Premises Outside Business Hours",
            "Cheque Fraud",
            "In the Custody of a Collector / Roundsman",
            "In the Custody of a Petrol Attendant",
            "In Transit By Employee(s)",
            "In Transit By Security Firm",
            "Official on Business Trip",
            "Officials Residence"
          ]
        ],
        ["Money", "Riot & Strike", ["NASRIA", "Outside RSA / NAM", "SASRIA"]],
        ["Money", "Death", ["Assault"]],
        //Office Contents
        ["Office Contents", "Accidental Damage", ["Documents", "Glass Furniture", "Power Surge"]],
        ["Office Contents", "Earthquake", ["Mining Operations", "Natural Phenomenon"]],
        ["Office Contents", "Explosion", ["Explosion"]],
        [
          "Office Contents",
          "Fire",
          [
            "Electrical Fault",
            "Explosion",
            "External source",
            "Fire General",
            "Mechanical Fault",
            "Poor Housekeeping / Lack of Care",
            "Source off Premises",
            "Spontaneous Combustion",
            "Subterranean"
          ]
        ],
        ["Office Contents", "Geyser", ["Repair", "Replace", "With Resultant Water Damage"]],
        ["Office Contents", "Locks & Keys", ["Locks & Keys"]],
        [
          "Office Contents",
          "Malicious Damage",
          ["Malicious Damage", "Malicious Damage With Theft"]
        ],
        ["Office Contents", "Riot & Strike", ["NASRIA", "Outside RSA / NAM", "SASRIA"]],
        ["Office Contents", "Storm", ["Hail / Snow", "Lightning", "Storm / Wind", "Water / Flood"]],
        ["Office Contents", "Subsidence & Landslip", ["Extended", "Limited"]],
        ["Office Contents", "Theft", ["From Premises", "Hijack"]],
        //Personal Liability
        [
          "Personal Liability",
          "CUP/PLIP",
          [
            "General - Additional Risks",
            "General - Difference in Conditions",
            "General - Excess Layer",
            "Motor - Additional Risks",
            "Motor - Difference in Conditions",
            "Motor - Excess Layer",
            "Property Owners - Additional Risks",
            "Property Owners - Difference in Conditions",
            "Property Owners - Excess Layer",
            "Tenants - Additional Risks",
            "Tenants - Difference in Conditions",
            "Tenants - Excess Layer",
            "Watercraft - Additional Risks",
            "Watercraft - Difference in Conditions",
            "Watercraft - Excess Layer"
          ]
        ],
        ["Personal Liability", "Death", ["Accidental"]],
        ["Personal Liability", "Injury", ["Accidental"]],
        ["Personal Liability", "Illness", ["Accidental"]],
        [
          "Personal Liability",
          "Liability",
          [
            "Bowls Full-House",
            "First Aid",
            "General",
            "Golfers Hole-in-One",
            "Lawn Mowers & Gardening Tools",
            "Neighbourhood Watch",
            "Security Firm",
            "Tenants Liability"
          ]
        ],
        //Plant
        ["Plant", "Earthquake", ["Mining Operations", "Natural Phenomenon"]],
        ["Plant", "Malicious Damage", ["Malicious Damage", "Malicious Damage With Theft"]],
        ["Plant", "Riot & Strike", ["NASRIA", "Outside RSA / NAM", "SASRIA"]],
        ["Plant", "Storm", ["Hail / Snow", "Lightning", "Storm / Wind", "Water / Flood"]],
        //Public Liability
        [
          "Public Liability",
          "CUP/PLIP",
          [
            "General - Additional Risks",
            "General - Difference in Conditions",
            "General - Excess Layer",
            "Motor - Additional Risks",
            "Motor - Difference in Conditions",
            "Motor - Excess Layer",
            "Property Owners - Additional Risks",
            "Property Owners - Difference in Conditions",
            "Property Owners - Excess Layer",
            "Tenants - Additional Risks",
            "Tenants - Difference in Conditions",
            "Tenants - Excess Layer",
            "Watercraft - Additional Risks",
            "Watercraft - Difference in Conditions",
            "Watercraft - Excess Layer"
          ]
        ],
        ["Public Liability", "Death", ["Accidental"]],
        ["Public Liability", "Injury", ["Accidental"]],
        ["Public Liability", "Illness", ["Accidental"]],
        [
          "Public Liability",
          "Liability",
          [
            "Animals - Auctions",
            "Animals - Driven / Escaped / Stray",
            "Animals - Grazing (not Insureds)",
            "Animals - Vehicles Drawn by",
            "Beauty Salon",
            "Breaking / Overflowing of Walls / Dams",
            "Car Parks",
            "Car Wash & Valet",
            "Chemist Prescription",
            "Crop Spraying",
            "Defective Workmanship",
            "Fire Spread - Other Than Sugar-Cane and Plantations",
            "Fire Spread - Sugar-Cane and Plantations",
            "First Aid",
            "Forecourt Service",
            "Game Hunting & Viewing",
            "General",
            "Government & Other Authorities Agreements",
            "Gratuitous Advice",
            "Hairdressers",
            "Hotel - Goods In Custody",
            "Lawn Mowers & Gardening Tools",
            "Neighbourhood Watch",
            "Products - Exported to E.E.C. Countries",
            "Products - Exported to Other Countries",
            "Products - Exported to U.S.A / Canada",
            "Products - General",
            "Products - Guarantee",
            "Products - Inefficacy",
            "Products - Recall",
            "Professional",
            "Schools",
            "Security Firm",
            "Statutory Legal Defence Costs",
            "Tenants Liability",
            "Tool of Trade",
            "Unattached Trailers",
            "Visitors & Employees Property",
            "Warehousemens Liability",
            "Work Away from Insured Premises",
            "Wrongful Act - Arrest & Defamation",
            "Wrongful Act - Directors",
            "Wrongful Act - Trustee"
          ]
        ],

        //Special Risks
        [
          "Special Risks",
          "Accidental Damage",
          [
            "Accidental Damage General",
            "Glass Buildings",
            "Impact by Aircraft / Aerial Devices",
            "Impact by Animals / Trees / Vehicles",
            "Power Surge",
            "Public Supply Connections",
            "Wild Animals Damage"
          ]
        ],
        ["Special Risks", "Claims Preparation Costs", ["Claims Preparation Costs"]],
        [
          "Special Risks",
          "CUP/PLIP",
          [
            "General - Additional Risks",
            "General - Difference in Conditions",
            "General - Excess Layer",
            "Motor - Additional Risks",
            "Motor - Difference in Conditions",
            "Motor - Excess Layer",
            "Property Owners - Additional Risks",
            "Property Owners - Difference in Conditions",
            "Property Owners - Excess Layer",
            "Tenants - Additional Risks",
            "Tenants - Difference in Conditions",
            "Tenants - Excess Layer",
            "Watercraft - Additional Risks",
            "Watercraft - Difference in Conditions",
            "Watercraft - Excess Layer"
          ]
        ],
        ["Special Risks", "Director & Officers Indemnity", ["Director & Officers Indemnity"]],
        ["Special Risks", "Earthquake", ["Mining Operations", "Natural Phenomenon"]],
        ["Special Risks", "Geyser", ["Repair", "Replace", "With Resultant Water Damage"]],
        ["Special Risks", "Explosion", ["Explosion"]],
        ["Special Risks", "Landscaped Gardens", ["Landscaped Gardens"]],
        [
          "Special Risks",
          "Leakage",
          [
            "Chemicals / Oils / Fumes",
            "Water - from Fire Extinguishing Installations",
            "Water - from Pipes",
            "Water - Leakage Costs"
          ]
        ],
        ["Special Risks", "Locks & Keys", ["Locks & Keys"]],
        ["Special Risks", "Malicious Damage", ["Malicious Damage", "Malicious Damage With Theft"]],
        ["Special Risks", "Riot & Strike", ["NASRIA", "Outside RSA / NAM", "SASRIA"]],
        ["Special Risks", "Storm", ["Hail / Snow", "Lightning", "Storm / Wind", "Water / Flood"]],
        ["Special Risks", "Subsidence & Landslip", ["Extended", "Limited"]],
        [
          "Special Risks",
          "Power Surge Cover to Non Electronic Equipment",
          ["Power Surge Cover to Non Electronic Equipment"]
        ],
        ["Special Risks", "Security Services", ["Security Services"]],
        [
          "Special Risks",
          "Theft",
          [
            "From Premises",
            "From Unattended Vehicle",
            "General",
            "Hijack",
            "Temporarily Removed from Building"
          ]
        ],
        [
          "Special Risks",
          "Vehicle - Accident",
          [
            "Animals / Trees",
            "Falling Objects",
            "Fixed Property",
            "Other vehicle(s)",
            "Parked - damaged by TP",
            "Pedestrian / Cyclist",
            "Potholes / Inappropriate road surface",
            "Single Vehicle Accident"
          ]
        ],
        ["Special Risks", "Vehicle - Glass", ["Repair", "Replace"]],
        [
          "Special Risks",
          "Vehicle - Roadside Assistance",
          [
            "Electrical Breakdown",
            "Flat Battery / Tyre",
            "Keys locked in vehicle",
            "Mechanical Breakdown"
          ]
        ],
        [
          "Special Risks",
          "Vehicle - Theft",
          [
            "Equipment & Damage to vehicle",
            "Equipment & No damage to vehicle",
            "Vehicle - Abscondence",
            "Vehicle - Hijack",
            "Vehicle - Theft"
          ]
        ],
        ["Special Risks", "Waiver of basic excess only", ["Waiver of basic excess only"]],
        ["Special Risks", "Excess buy back", ["Excess buy back"]],
        ["Special Risks", "Wear and Tear on Internal Pipes", ["Wear and Tear on Internal Pipes"]],
        [
          "Special Risks",
          "Fire",
          [
            "Electrical Fault",
            "Explosion",
            "External source",
            "Fire General",
            "Mechanical Fault",
            "Poor Housekeeping / Lack of Care",
            "Source off Premises",
            "Spontaneous Combustion",
            "Subterranean"
          ]
        ],
        //Stated Benefits
        [
          "Stated Benefits",
          "Death",
          [
            "Accidental",
            "Building / Premises",
            "Assault",
            "Machinery / Equipment",
            "Road Accident",
            "Toxic Processes",
            "Unhygienic Conditions",
            "Passive War"
          ]
        ],
        [
          "Stated Benefits",
          "Illness",
          [
            "Accidental",
            "Building / Premises",
            "Assault",
            "Machinery / Equipment",
            "Road Accident",
            "Toxic Processes",
            "Unhygienic Conditions",
            "Passive War"
          ]
        ],
        [
          "Stated Benefits",
          "Injury",
          [
            "Accidental",
            "Building / Premises",
            "Assault",
            "Machinery / Equipment",
            "Road Accident",
            "Toxic Processes",
            "Unhygienic Conditions"
          ]
        ],
        ["Stated Benefits", "Medical Expenses", ["Accidental", "Assault", "Road Accident"]],
        //Theft
        ["Theft", "Locks & Keys", ["Locks & Keys"]],
        ["Theft", "Theft", ["General", "From Premises"]],

        //Watercraft
        ["Watercraft", "Earthquake", ["Mining Operations", "Natural Phenomenon"]],
        ["Watercraft", "Explosion", ["Explosion"]],
        ["Watercraft", "Malicious Damage", ["Malicious Damage", "Malicious Damage With Theft"]],
        [
          "Watercraft",
          "Marine",
          [
            "Accidental Damage",
            "Capsizing",
            "Force of Nature",
            "Impact Damage",
            "Machinery Damage",
            "Malicious Damage",
            "Outboards Overboard",
            "Pirating",
            "Repairs Negligence",
            "Shortages / Non delivery of Cargo",
            "Sinking",
            "Stranding",
            "Submerged Object Struck",
            "War"
          ]
        ],
        ["Watercraft", "Riot & Strike", ["NASRIA", "Outside RSA / NAM", "SASRIA"]],
        ["Watercraft", "Storm", ["Hail / Snow", "Lightning", "Storm / Wind", "Water / Flood"]],
        ["Watercraft", "Theft", ["General", "From Premises", "Hijack"]]
      ],
      schemaIndex: 3,
      action: () => removeSchemasByTitle("Loss Caused by Other Parties")
    },
    //Property Damage - form 5
    {
      causes: [
        //"Accidental Damage"
        [
          "Accidental Damage",
          "Accidental Damage",
          [
            "Accidental Damage General",
            "Aerial Fittings",
            "Impact by Aircraft / Aerial Devices",
            "Lost / Misplaced",
            "Power Surge"
          ]
        ],
        //Accounts Receivable
        ["Accounts Receivable", "Accidental Damage", ["Accidental Damage General"]],
        ["Accounts Receivable", "Earthquake", ["Mining Operations", "Natural Phenomenon"]],
        ["Accounts Receivable", "Leakage", ["Water - from Pipes"]],
        ["Accounts Receivable", "Fire", ["Source off Premises"]],
        [
          "Accounts Receivable",
          "Malicious Damage",
          ["Malicious Damage", "Malicious Damage With Theft"]
        ],
        ["Accounts Receivable", "Riot & Strike", ["NASRIA", "Outside RSA / NAM", "SASRIA"]],
        [
          "Accounts Receivable",
          "Storm",
          ["Hail / Snow", "Lightning", "Storm / Wind", "Water / Flood"]
        ],
        ["Accounts Receivable", "Subsidence & Landslip", ["Extended", "Limited"]],
        ["Accounts Receivable", "Theft", ["General"]],
        //all risk
        ["All Risk", "Accidental Damage", ["Accidental Damage General", "Lost / Misplaced"]],
        ["All Risk", "Earthquake", ["Mining Operations", "Natural Phenomenon"]],
        ["All Risk", "Explosion", ["Explosion"]],
        [
          "All Risk",
          "Fire",
          [
            "Electrical Fault",
            "Explosion",
            "External source",
            "Fire General",
            "Mechanical Fault",
            "Poor Housekeeping / Lack of Care",
            "Source off Premises",
            "Spontaneous Combustion",
            "Subterranean"
          ]
        ],
        [
          "All Risk",
          "Leakage",
          [
            "Chemicals / Oils / Fumes",
            "Water - from Fire Extinguishing Installations",
            "Water - from Pipes"
          ]
        ],
        ["All Risk", "Malicious Damage", ["Malicious Damage", "Malicious Damage With Theft"]],
        ["All Risk", "Riot & Strike", ["NASRIA", "Outside RSA / NAM", "SASRIA"]],
        ["All Risk", "Storm", ["Hail / Snow", "Lightning", "Storm / Wind", "Water / Flood"]],
        ["All Risk", "Subsidence & Landslip", ["Extended", "Limited"]],
        [
          "All Risk",
          "Theft",
          [
            "From Premises",
            "From Unattended Vehicle",
            "General",
            "Hijack",
            "Temporarily Removed from Building",
            "With Force",
            "Without Force"
          ]
        ],
        //body corporate
        [
          "Body Corporate",
          "Accidental Damage",
          [
            "Aerial Fittings",
            "Accidental Damage General",
            "Domestic Machinery",
            "Glass Buildings",
            "Glass Sanitaryware",
            "Impact by Aircraft / Aerial Devices",
            "Impact by Animals / Trees / Vehicles",
            "Power Surge",
            "Public Supply Connections",
            "Tree Removal Costs",
            "Wild Animals Damage"
          ]
        ],
        ["Body Corporate", "Death", ["Accidental", "Building / Premises"]],
        ["Body Corporate", "Earthquake", ["Mining Operations", "Natural Phenomenon"]],
        ["Body Corporate", "Explosion", ["Explosion"]],
        [
          "Body Corporate",
          "Fire",
          [
            "Electrical Fault",
            "Explosion",
            "External source",
            "Fire General",
            "Mechanical Fault",
            "Poor Housekeeping / Lack of Care",
            "Source off Premises",
            "Spontaneous Combustion",
            "Subterranean"
          ]
        ],
        [
          "Body Corporate",
          "Geyser",
          ["Repair", "Replacement", "With Resultant Water Damage", "Replace"]
        ],
        ["Body Corporate", "Illness", ["Accidental", "Building / Premises"]],
        ["Body Corporate", "Injury", ["Accidental", "Building / Premises"]],
        [
          "Body Corporate",
          "Leakage",
          [
            "Chemicals / Oils / Fumes",
            "Water - from Fire Extinguishing Installations",
            "Water - from Pipes",
            "Water - Leakage Costs"
          ]
        ],
        ["Body Corporate", "Liability", ["General", "Security Firm"]],
        ["Body Corporate", "Locks & Keys", ["Locks & Keys"]],
        ["Body Corporate", "Malicious Damage", ["Malicious Damage", "Malicious Damage With Theft"]],
        ["Body Corporate", "Riot & Strike", ["NASRIA", "Outside RSA / NAM", "SASRIA"]],
        ["Body Corporate", "Storm", ["Hail / Snow", "Lightning", "Storm / Wind", "Water / Flood"]],
        ["Body Corporate", "Subsidence & Landslip", ["Extended", "Limited"]],
        [
          "Body Corporate",
          "Theft",
          ["From Premises", "From Unattended Vehicle", "General", "With Force", "Without Force"]
        ],
        //Buildings Combined
        [
          "Buildings Combined",
          "Accidental Damage",
          [
            "Aerial Fittings",
            "Accidental Damage General",
            "Domestic Machinery",
            "Glass Buildings",
            "Glass Sanitaryware",
            "Impact by Aircraft / Aerial Devices",
            "Impact by Animals / Trees / Vehicles",
            "Power Surge",
            "Public Supply Connections",
            "Tree Removal Costs",
            "Wild Animals Damage"
          ]
        ],
        ["Buildings Combined", "Earthquake", ["Mining Operations", "Natural Phenomenon"]],
        ["Buildings Combined", "Fire", ["Source off Premises"]],
        ["Buildings Combined", "Locks & Keys", ["Locks & Keys"]],
        ["Buildings Combined", "Liability", ["General", "Security Firm"]],
        [
          "Buildings Combined",
          "Malicious Damage",
          ["Malicious Damage", "Malicious Damage With Theft"]
        ],
        ["Buildings Combined", "Riot & Strike", ["NASRIA", "Outside RSA / NAM", "SASRIA"]],
        [
          "Buildings Combined",
          "Storm",
          ["Hail / Snow", "Lightning", "Storm / Wind", "Water / Flood"]
        ],
        ["Buildings Combined", "Subsidence & Landslip", ["Extended", "Limited"]],
        [
          "Buildings Combined",
          "Theft",
          ["From Premises", "From Unattended Vehicle", "General", "With Force", "Without Force"]
        ],

        //Business All Risks
        [
          "Business All Risks",
          "Accidental Damage",
          ["Accidental Damage General", "Lost / Misplaced"]
        ],
        ["Business All Risks", "Earthquake", ["Mining Operations", "Natural Phenomenon"]],
        ["Business All Risks", "Fire", ["Source off Premises"]],
        [
          "Business All Risks",
          "Malicious Damage",
          ["Malicious Damage", "Malicious Damage With Theft"]
        ],
        ["Business All Risks", "Riot & Strike", ["NASRIA", "Outside RSA / NAM", "SASRIA"]],
        [
          "Business All Risks",
          "Storm",
          ["Hail / Snow", "Lightning", "Storm / Wind", "Water / Flood"]
        ],
        ["Business All Risks", "Subsidence & Landslip", ["Extended", "Limited"]],
        [
          "Business All Risks",
          "Theft",
          [
            "General",
            "From Premises",
            "With Force",
            "Without Force",
            "From Unattended Vehicle",
            "Hijack",
            "Temporarily Removed from Building"
          ]
        ],

        //Business Interruption
        [
          "Business Interruption",
          "Accidental Damage",
          ["Accidental Damage General", "Deterioration of Stock"]
        ],
        ["Business Interruption", "Earthquake", ["Mining Operations", "Natural Phenomenon"]],
        ["Business Interruption", "Fire", ["Source off Premises"]],
        [
          "Business Interruption",
          "Malicious Damage",
          ["Malicious Damage", "Malicious Damage With Theft"]
        ],
        ["Business Interruption", "Riot & Strike", ["NASRIA", "Outside RSA / NAM", "SASRIA"]],
        [
          "Business Interruption",
          "Storm",
          ["Hail / Snow", "Lightning", "Storm / Wind", "Water / Flood"]
        ],
        ["Business Interruption", "Subsidence & Landslip", ["Extended", "Limited"]],
        [
          "Business Interruption",
          "Theft",
          ["From Premises", "From Unattended Vehicle", "General", "With Force", "Without Force"]
        ],

        //Contract Works
        ["Contract Works", "Accidental Damage", ["Accidental Damage General"]],
        ["Contract Works", "Earthquake", ["Mining Operations", "Natural Phenomenon"]],
        ["Contract Works", "Fire", ["Source off Premises"]],
        ["Contract Works", "Malicious Damage", ["Malicious Damage", "Malicious Damage With Theft"]],
        ["Contract Works", "Riot & Strike", ["NASRIA", "Outside RSA / NAM", "SASRIA"]],
        ["Contract Works", "Storm", ["Hail / Snow", "Lightning", "Storm / Wind", "Water / Flood"]],
        ["Contract Works", "Subsidence & Landslip", ["Extended", "Limited"]],
        [
          "Contract Works",
          "Theft",
          ["From Premises", "From Unattended Vehicle", "General", "With Force", "Without Force"]
        ],

        //Electronic Equipment
        ["Electronic Equipment", "Accidental Damage", ["Accidental Damage General", "Power Surge"]],
        ["Electronic Equipment", "Earthquake", ["Mining Operations", "Natural Phenomenon"]],
        ["Electronic Equipment", "Fire", ["Source off Premises"]],
        [
          "Electronic Equipment",
          "Malicious Damage",
          ["Malicious Damage", "Malicious Damage With Theft"]
        ],
        ["Electronic Equipment", "Riot & Strike", ["NASRIA", "Outside RSA / NAM", "SASRIA"]],
        [
          "Electronic Equipment",
          "Storm",
          ["Hail / Snow", "Lightning", "Storm / Wind", "Water / Flood"]
        ],
        ["Electronic Equipment", "Subsidence & Landslip", ["Extended", "Limited"]],
        [
          "Electronic Equipment",
          "Theft",
          [
            "From Premises",
            "From Unattended Vehicle",
            "General",
            "With Force",
            "Without Force",
            "Hijack",
            "Temporarily Removed from Building"
          ]
        ],

        //Employers Liability
        [
          "Employers Liability",
          "Death",
          [
            "Accidental",
            "Building / Premises",
            "Assault",
            "Machinery / Equipment",
            "Road Accident",
            "Toxic Processes",
            "Unhygienic Conditions"
          ]
        ],
        [
          "Employers Liability",
          "Illness",
          [
            "Accidental",
            "Building / Premises",
            "Assault",
            "Machinery / Equipment",
            "Road Accident",
            "Toxic Processes",
            "Unhygienic Conditions"
          ]
        ],
        [
          "Employers Liability",
          "Injury",
          [
            "Accidental",
            "Building / Premises",
            "Assault",
            "Machinery / Equipment",
            "Road Accident",
            "Toxic Processes",
            "Unhygienic Conditions"
          ]
        ],
        //Fidelity Guarantee
        ["Fidelity Guarantee", "Theft", ["Employee Fidelity"]],
        //Fire
        [
          "Fire",
          "Accidental Damage",
          [
            "Impact by Aircraft / Aerial Devices",
            "Impact by Animals / Trees / Vehicles",
            "Power Surge",
            "Public Supply Connections",
            "Wild Animals Damage"
          ]
        ],
        ["Fire", "Earthquake", ["Mining Operations", "Natural Phenomenon"]],
        ["Fire", "Explosion", ["Explosion"]],
        [
          "Fire",
          "Fire",
          [
            "Electrical Fault",
            "Explosion",
            "External source",
            "Fire General",
            "Mechanical Fault",
            "Poor Housekeeping / Lack of Care",
            "Source off Premises",
            "Spontaneous Combustion",
            "Subterranean"
          ]
        ],
        ["Fire", "Geyser", ["Repair", "Replace", "With Resultant Water Damage"]],
        ["Fire", "Leakage", ["Water - from Fire Extinguishing Installations"]],
        ["Fire", "Malicious Damage", ["Malicious Damage", "Malicious Damage With Theft"]],
        ["Fire", "Riot & Strike", ["NASRIA", "Outside RSA / NAM", "SASRIA"]],
        ["Fire", "Storm", ["Hail / Snow", "Lightning", "Storm / Wind", "Water / Flood"]],
        ["Fire", "Subsidence & Landslip", ["Extended", "Limited"]],

        //Glass
        ["Glass", "Accidental Damage", ["Accidental Damage General", "Glass Buildings"]],
        ["Glass", "Malicious Damage", ["Malicious Damage", "Malicious Damage With Theft"]],
        ["Glass", "Riot & Strike", ["NASRIA", "Outside RSA / NAM", "SASRIA"]],
        //Goods In Transit
        [
          "Goods In Transit",
          "Accidental Damage",
          [
            "Accidental Damage General",
            "GIT Collission / Overturning / Derailment",
            "GIT Falling off conveyance"
          ]
        ],
        ["Goods In Transit", "Earthquake", ["Mining Operations", "Natural Phenomenon"]],
        ["Goods In Transit", "Fire", ["Source off Premises"]],
        [
          "Goods In Transit",
          "Malicious Damage",
          ["Malicious Damage", "Malicious Damage With Theft"]
        ],
        ["Goods In Transit", "Riot & Strike", ["NASRIA", "Outside RSA / NAM", "SASRIA"]],
        [
          "Goods In Transit",
          "Storm",
          ["Hail / Snow", "Lightning", "Storm / Wind", "Water / Flood"]
        ],
        ["Goods In Transit", "Subsidence & Landslip", ["Extended", "Limited"]],
        [
          "Goods In Transit",
          "Theft",
          ["From Unattended Vehicle", "General", "Hijack", "With Force", "Without Force"]
        ],
        //Group Personal Accident
        [
          "Group Personal Accident",
          "Death",
          [
            "Accidental",
            "Building / Premises",
            "Assault",
            "Machinery / Equipment",
            "Passive War",
            "Road Accident",
            "Toxic Processes",
            "Unhygienic Conditions"
          ]
        ],
        [
          "Group Personal Accident",
          "Illness",
          [
            "Accidental",
            "Building / Premises",
            "Assault",
            "Machinery / Equipment",
            "Road Accident",
            "Passive War",
            "Toxic Processes",
            "Unhygienic Conditions"
          ]
        ],
        [
          "Group Personal Accident",
          "Injury",
          [
            "Accidental",
            "Building / Premises",
            "Assault",
            "Machinery / Equipment",
            "Passive War",
            "Road Accident",
            "Toxic Processes",
            "Unhygienic Conditions"
          ]
        ],
        ["Group Personal Accident", "Medical Expenses", ["Accidental", "Assault", "Road Accident"]],
        //Homeowners
        [
          "Homeowners",
          "Accidental Damage",
          [
            "Accidental Damage General",
            "Aerial Fittings",
            "Domestic Machinery",
            "Glass Buildings",
            "Glass Sanitaryware",
            "Impact by Aircraft / Aerial Devices",
            "Impact by Animals / Trees / Vehicles",
            "Public Supply Connections",
            "Tree Removal Costs",
            "Wild Animals Damage"
          ]
        ],
        ["Homeowners", "Earthquake", ["Mining Operations", "Natural Phenomenon"]],
        ["Homeowners", "Fire", ["Source off Premises"]],
        ["Homeowners", "Locks & Keys", ["Locks & Keys"]],
        ["Homeowners", "Malicious Damage", ["Malicious Damage", "Malicious Damage With Theft"]],
        ["Homeowners", "Riot & Strike", ["NASRIA", "Outside RSA / NAM", "SASRIA"]],
        ["Homeowners", "Storm", ["Hail / Snow", "Lightning", "Storm / Wind", "Water / Flood"]],
        ["Homeowners", "Subsidence & Landslip", ["Extended", "Limited"]],
        [
          "Homeowners",
          "Theft",
          [
            "From Premises",
            "From Unattended Vehicle",
            "General",
            "Hijack",
            "Temporarily Removed from Building",
            "With Force",
            "Without Force"
          ]
        ],
        //House Holders
        [
          "House Holders",
          "Accidental Damage",
          [
            "Accidental Damage General",
            "Aerial Fittings",
            "Audio Visual Equipment",
            "Deterioration of Stock",
            "Domestic Machinery",
            "Glass Furniture",
            "Impact by Aircraft / Aerial Devices",
            "Impact by Animals / Trees / Vehicles",
            "In Transit",
            "Power Surge"
          ]
        ],
        ["House Holders", "Earthquake", ["Mining Operations", "Natural Phenomenon"]],

        ["House Holders", "Fire", ["Source off Premises"]],

        ["House Holders", "Locks & Keys", ["Locks & Keys"]],
        ["House Holders", "Malicious Damage", ["Malicious Damage", "Malicious Damage With Theft"]],
        [
          "House Holders",
          "Machinery Breakdown",
          ["Electrical Breakdown", "Electronical Breakdown", "Mechanical Breakdown"]
        ],
        ["House Holders", "Medical Expenses", ["Accidental"]],
        ["House Holders", "Riot & Strike", ["NASRIA", "Outside RSA / NAM", "SASRIA"]],
        ["House Holders", "Storm", ["Hail / Snow", "Lightning", "Storm / Wind", "Water / Flood"]],
        ["House Holders", "Subsidence & Landslip", ["Extended", "Limited"]],
        [
          "House Holders",
          "Theft",
          [
            "From Unattended Vehicle",
            "From Premises",
            "General",
            "Temporarily Removed from Building",
            "With Force",
            "Without Force"
          ]
        ],

        //Machinery Breakdown
        [
          "Machinery Breakdown",
          "Accidental Damage",
          ["Accidental Damage General", "Deterioration of Stock", "Power Surge"]
        ],
        ["Machinery Breakdown", "Explosion", ["Explosion"]],
        ["Machinery Breakdown", "Fire", ["Electrical Fault", "Explosion"]],
        [
          "Machinery Breakdown",
          "Machinery Breakdown",
          [
            "Centrifugal forces",
            "Electrical Breakdown",
            "Electronical Breakdown",
            "Excessive Electrical Pressure",
            "Faulty Casting, Construction, Erection",
            "Faulty Design",
            "Faulty Material",
            "Faulty Workmanship, Lack of Skill, Carelessness",
            "Loosening of Parts",
            "Mechanical Breakdown",
            "Obstruction or entry of foreign bodies",
            "Short Circuit",
            "Shortage of Water in Boilers",
            "Vibration, mal-adjustment, mal-alignment"
          ]
        ],

        //Money
        ["Money", "Injury", ["Assault"]],
        ["Money", "Medical Expenses", ["Assault"]],
        ["Money", "Locks & Keys", ["Locks & Keys"]],
        [
          "Money",
          "Money Loss",
          [
            "At Insured Premises During Business Hours",
            "At Insured Premises Outside Business Hours",
            "Cheque Fraud",
            "In the Custody of a Collector / Roundsman",
            "In the Custody of a Petrol Attendant",
            "In Transit By Employee(s)",
            "In Transit By Security Firm",
            "Official on Business Trip",
            "Officials Residence"
          ]
        ],
        ["Money", "Riot & Strike", ["NASRIA", "Outside RSA / NAM", "SASRIA"]],
        ["Money", "Death", ["Assault"]],
        //Office Contents
        [
          "Office Contents",
          "Accidental Damage",
          [
            "Accidental Damage General",
            "Documents",
            "Glass Furniture",
            "Impact by Aircraft / Aerial Devices",
            "Impact by Animals / Trees / Vehicles",
            "Power Surge"
          ]
        ],
        ["Office Contents", "Earthquake", ["Mining Operations", "Natural Phenomenon"]],

        ["Office Contents", "Fire", ["Source off Premises"]],
        ["Office Contents", "Locks & Keys", ["Locks & Keys"]],
        [
          "Office Contents",
          "Malicious Damage",
          ["Malicious Damage", "Malicious Damage With Theft"]
        ],
        ["Office Contents", "Riot & Strike", ["NASRIA", "Outside RSA / NAM", "SASRIA"]],
        ["Office Contents", "Storm", ["Hail / Snow", "Lightning", "Storm / Wind", "Water / Flood"]],
        ["Office Contents", "Subsidence & Landslip", ["Extended", "Limited"]],
        [
          "Office Contents",
          "Theft",
          [
            "From Unattended Vehicle",
            "From Premises",
            "General",
            "Temporarily Removed from Building",
            "With Force",
            "Without Force"
          ]
        ],

        //Personal Liability
        [
          "Personal Liability",
          "CUP/PLIP",
          [
            "General - Additional Risks",
            "General - Difference in Conditions",
            "General - Excess Layer",
            "Motor - Additional Risks",
            "Motor - Difference in Conditions",
            "Motor - Excess Layer",
            "Property Owners - Additional Risks",
            "Property Owners - Difference in Conditions",
            "Property Owners - Excess Layer",
            "Tenants - Additional Risks",
            "Tenants - Difference in Conditions",
            "Tenants - Excess Layer",
            "Watercraft - Additional Risks",
            "Watercraft - Difference in Conditions",
            "Watercraft - Excess Layer"
          ]
        ],
        ["Personal Liability", "Death", ["Accidental"]],
        ["Personal Liability", "Injury", ["Accidental"]],
        ["Personal Liability", "Illness", ["Accidental"]],
        [
          "Personal Liability",
          "Liability",
          [
            "Bowls Full-House",
            "First Aid",
            "General",
            "Golfers Hole-in-One",
            "Lawn Mowers & Gardening Tools",
            "Neighbourhood Watch",
            "Security Firm",
            "Tenants Liability"
          ]
        ],

        //Plant
        [
          "Plant",
          "Accidental Damage",
          [
            "Accidental Damage General",
            "GIT Collission / Overturning / Derailment",
            "GIT Falling off conveyance",
            "Impact by Aircraft / Aerial Devices"
          ]
        ],
        ["Plant", "Earthquake", ["Mining Operations", "Natural Phenomenon"]],
        ["Plant", "Fire", ["Source off Premises"]],

        ["Plant", "Malicious Damage", ["Malicious Damage", "Malicious Damage With Theft"]],
        ["Plant", "Riot & Strike", ["NASRIA", "Outside RSA / NAM", "SASRIA"]],
        ["Plant", "Storm", ["Hail / Snow", "Lightning", "Storm / Wind", "Water / Flood"]],
        [
          "Plant",
          "Theft",
          [
            "From Unattended Vehicle",
            "From Premises",
            "General",
            "Temporarily Removed from Building",
            "With Force",
            "Without Force"
          ]
        ],

        //Public Liability
        [
          "Public Liability",
          "CUP/PLIP",
          [
            "General - Additional Risks",
            "General - Difference in Conditions",
            "General - Excess Layer",
            "Motor - Additional Risks",
            "Motor - Difference in Conditions",
            "Motor - Excess Layer",
            "Property Owners - Additional Risks",
            "Property Owners - Difference in Conditions",
            "Property Owners - Excess Layer",
            "Tenants - Additional Risks",
            "Tenants - Difference in Conditions",
            "Tenants - Excess Layer",
            "Watercraft - Additional Risks",
            "Watercraft - Difference in Conditions",
            "Watercraft - Excess Layer"
          ]
        ],
        ["Public Liability", "Death", ["Accidental"]],
        ["Public Liability", "Injury", ["Accidental"]],
        ["Public Liability", "Illness", ["Accidental"]],
        [
          "Public Liability",
          "Liability",
          [
            "Animals - Auctions",
            "Animals - Driven / Escaped / Stray",
            "Animals - Grazing (not Insureds)",
            "Animals - Vehicles Drawn by",
            "Beauty Salon",
            "Breaking / Overflowing of Walls / Dams",
            "Car Parks",
            "Car Wash & Valet",
            "Chemist Prescription",
            "Crop Spraying",
            "Defective Workmanship",
            "Fire Spread - Other Than Sugar-Cane and Plantations",
            "Fire Spread - Sugar-Cane and Plantations",
            "First Aid",
            "Forecourt Service",
            "Game Hunting & Viewing",
            "General",
            "Government & Other Authorities Agreements",
            "Gratuitous Advice",
            "Hairdressers",
            "Hotel - Goods In Custody",
            "Lawn Mowers & Gardening Tools",
            "Neighbourhood Watch",
            "Products - Exported to E.E.C. Countries",
            "Products - Exported to Other Countries",
            "Products - Exported to U.S.A / Canada",
            "Products - General",
            "Products - Guarantee",
            "Products - Inefficacy",
            "Products - Recall",
            "Professional",
            "Schools",
            "Security Firm",
            "Statutory Legal Defence Costs",
            "Tenants Liability",
            "Tool of Trade",
            "Unattached Trailers",
            "Visitors & Employees Property",
            "Warehousemens Liability",
            "Work Away from Insured Premises",
            "Wrongful Act - Arrest & Defamation",
            "Wrongful Act - Directors",
            "Wrongful Act - Trustee"
          ]
        ],

        //Special Risks
        [
          "Special Risks",
          "Accidental Damage",
          [
            "Accidental Damage General",
            "Glass Buildings",
            "Impact by Aircraft / Aerial Devices",
            "Impact by Animals / Trees / Vehicles",
            "Power Surge",
            "Public Supply Connections",
            "Wild Animals Damage"
          ]
        ],
        ["Special Risks", "Claims Preparation Costs", ["Claims Preparation Costs"]],
        [
          "Special Risks",
          "CUP/PLIP",
          [
            "General - Additional Risks",
            "General - Difference in Conditions",
            "General - Excess Layer",
            "Motor - Additional Risks",
            "Motor - Difference in Conditions",
            "Motor - Excess Layer",
            "Property Owners - Additional Risks",
            "Property Owners - Difference in Conditions",
            "Property Owners - Excess Layer",
            "Tenants - Additional Risks",
            "Tenants - Difference in Conditions",
            "Tenants - Excess Layer",
            "Watercraft - Additional Risks",
            "Watercraft - Difference in Conditions",
            "Watercraft - Excess Layer"
          ]
        ],
        ["Special Risks", "Director & Officers Indemnity", ["Director & Officers Indemnity"]],
        ["Special Risks", "Earthquake", ["Mining Operations", "Natural Phenomenon"]],
        ["Special Risks", "Explosion", ["Explosion"]],
        ["Special Risks", "Geyser", ["Repair", "Replace", "With Resultant Water Damage"]],
        ["Special Risks", "Landscaped Gardens", ["Landscaped Gardens"]],
        [
          "Special Risks",
          "Leakage",
          [
            "Chemicals / Oils / Fumes",
            "Water - from Fire Extinguishing Installations",
            "Water - from Pipes",
            "Water - Leakage Costs"
          ]
        ],
        ["Special Risks", "Locks & Keys", ["Locks & Keys"]],
        ["Special Risks", "Malicious Damage", ["Malicious Damage", "Malicious Damage With Theft"]],
        ["Special Risks", "Riot & Strike", ["NASRIA", "Outside RSA / NAM", "SASRIA"]],
        ["Special Risks", "Storm", ["Hail / Snow", "Lightning", "Storm / Wind", "Water / Flood"]],
        ["Special Risks", "Subsidence & Landslip", ["Extended", "Limited"]],
        [
          "Special Risks",
          "Power Surge Cover to Non Electronic Equipment",
          ["Power Surge Cover to Non Electronic Equipment"]
        ],
        ["Special Risks", "Security Services", ["Security Services"]],
        [
          "Special Risks",
          "Theft",
          [
            "From Premises",
            "From Unattended Vehicle",
            "General",
            "Hijack",
            "Temporarily Removed from Building"
          ]
        ],
        [
          "Special Risks",
          "Vehicle - Accident",
          [
            "Animals / Trees",
            "Falling Objects",
            "Fixed Property",
            "Other vehicle(s)",
            "Parked - damaged by TP",
            "Pedestrian / Cyclist",
            "Potholes / Inappropriate road surface",
            "Single Vehicle Accident"
          ]
        ],
        ["Special Risks", "Vehicle - Glass", ["Repair", "Replace"]],
        [
          "Special Risks",
          "Vehicle - Roadside Assistance",
          [
            "Electrical Breakdown",
            "Flat Battery / Tyre",
            "Keys locked in vehicle",
            "Mechanical Breakdown"
          ]
        ],
        [
          "Special Risks",
          "Vehicle - Theft",
          [
            "Equipment & Damage to vehicle",
            "Equipment & No damage to vehicle",
            "Vehicle - Abscondence",
            "Vehicle - Hijack",
            "Vehicle - Theft"
          ]
        ],
        ["Special Risks", "Waiver of basic excess only", ["Waiver of basic excess only"]],
        ["Special Risks", "Wear and Tear on Internal Pipes", ["Wear and Tear on Internal Pipes"]],
        ["Special Risks", "Excess buy back", ["Excess buy back"]],
        [
          "Special Risks",
          "Fire",
          [
            "Electrical Fault",
            "Explosion",
            "External source",
            "Fire General",
            "Mechanical Fault",
            "Poor Housekeeping / Lack of Care",
            "Source off Premises",
            "Spontaneous Combustion",
            "Subterranean"
          ]
        ],
        //Stated Benefits
        [
          "Stated Benefits",
          "Death",
          [
            "Accidental",
            "Building / Premises",
            "Assault",
            "Machinery / Equipment",
            "Road Accident",
            "Toxic Processes",
            "Unhygienic Conditions",
            "Passive War"
          ]
        ],
        [
          "Stated Benefits",
          "Illness",
          [
            "Accidental",
            "Building / Premises",
            "Assault",
            "Machinery / Equipment",
            "Road Accident",
            "Toxic Processes",
            "Unhygienic Conditions",
            "Passive War"
          ]
        ],
        [
          "Stated Benefits",
          "Injury",
          [
            "Accidental",
            "Building / Premises",
            "Assault",
            "Machinery / Equipment",
            "Road Accident",
            "Toxic Processes",
            "Unhygienic Conditions"
          ]
        ],
        ["Stated Benefits", "Medical Expenses", ["Accidental", "Assault", "Road Accident"]],
        //Theft
        ["Theft", "Locks & Keys", ["Locks & Keys"]],
        ["Theft", "Theft", ["From Premises", "General", "Hijack", "With Force", "Without Force"]],
        //Watercraft
        [
          "Watercraft",
          "Accidental Damage",
          [
            "Accidental Damage General",
            "GIT Collission / Overturning / Derailment",
            "GIT Falling off conveyance",
            "Impact by Aircraft / Aerial Devices"
          ]
        ],
        ["Watercraft", "Earthquake", ["Mining Operations", "Natural Phenomenon"]],
        ["Watercraft", "Fire", ["Source off Premises"]],
        ["Watercraft", "Malicious Damage", ["Malicious Damage", "Malicious Damage With Theft"]],
        [
          "Watercraft",
          "Marine",
          [
            "Accidental Damage",
            "Capsizing",
            "Force of Nature",
            "Impact Damage",
            "Machinery Damage",
            "Malicious Damage",
            "Outboards Overboard",
            "Pirating",
            "Repairs Negligence",
            "Shortages / Non delivery of Cargo",
            "Sinking",
            "Stranding",
            "Submerged Object Struck",
            "War"
          ]
        ],
        ["Watercraft", "Medical Expenses", ["Accidental"]],
        ["Watercraft", "Riot & Strike", ["NASRIA", "Outside RSA / NAM", "SASRIA"]],
        ["Watercraft", "Storm", ["Hail / Snow", "Lightning", "Storm / Wind", "Water / Flood"]],
        [
          "Watercraft",
          "Theft",
          ["General", "From Premises", "Hijack", "With Force", "Without Force"]
        ]
      ],
      schemaIndex: 4,
      action: () => removeSchemasByTitle("Property Damage")
    },
    //Personal Injuries -form 6
    {
      causes: [
        //"Accidental Damage"
        ["Accidental Damage", "Accidental Damage", ["Lost / Misplaced", "Power Surge"]],
        //Accounts Receivable
        ["Accounts Receivable", "Earthquake", ["Mining Operations", "Natural Phenomenon"]],
        ["Accounts Receivable", "Fire", ["Source off Premises"]],
        [
          "Accounts Receivable",
          "Malicious Damage",
          ["Malicious Damage", "Malicious Damage With Theft"]
        ],
        ["Accounts Receivable", "Riot & Strike", ["NASRIA", "Outside RSA / NAM", "SASRIA"]],
        [
          "Accounts Receivable",
          "Storm",
          ["Hail / Snow", "Lightning", "Storm / Wind", "Water / Flood"]
        ],
        ["Accounts Receivable", "Subsidence & Landslip", ["Extended", "Limited"]],
        ["Accounts Receivable", "Theft", ["General"]],
        //all risk
        ["All Risk", "Accidental Damage", ["Accidental Damage General", "Lost / Misplaced"]],
        ["All Risk", "Earthquake", ["Mining Operations", "Natural Phenomenon"]],
        ["All Risk", "Explosion", ["Explosion"]],
        [
          "All Risk",
          "Fire",
          [
            "Electrical Fault",
            "Explosion",
            "External source",
            "Fire General",
            "Mechanical Fault",
            "Poor Housekeeping / Lack of Care",
            "Source off Premises",
            "Spontaneous Combustion",
            "Subterranean"
          ]
        ],
        [
          "All Risk",
          "Leakage",
          [
            "Chemicals / Oils / Fumes",
            "Water - from Fire Extinguishing Installations",
            "Water - from Pipes"
          ]
        ],
        ["All Risk", "Malicious Damage", ["Malicious Damage", "Malicious Damage With Theft"]],
        ["All Risk", "Riot & Strike", ["NASRIA", "Outside RSA / NAM", "SASRIA"]],
        ["All Risk", "Storm", ["Hail / Snow", "Lightning", "Storm / Wind", "Water / Flood"]],
        ["All Risk", "Subsidence & Landslip", ["Extended", "Limited"]],
        [
          "All Risk",
          "Theft",
          [
            "From Premises",
            "From Unattended Vehicle",
            "General",
            "Hijack",
            "Temporarily Removed from Building",
            "With Force",
            "Without Force"
          ]
        ],
        //body corporate
        [
          "Body Corporate",
          "Accidental Damage",
          [
            "Aerial Fittings",
            "Accidental Damage General",
            "Domestic Machinery",
            "Glass Buildings",
            "Glass Sanitaryware",
            "Impact by Aircraft / Aerial Devices",
            "Impact by Animals / Trees / Vehicles",
            "Power Surge",
            "Public Supply Connections",
            "Tree Removal Costs",
            "Wild Animals Damage"
          ]
        ],
        ["Body Corporate", "Death", ["Accidental", "Building / Premises"]],
        ["Body Corporate", "Earthquake", ["Mining Operations", "Natural Phenomenon"]],
        ["Body Corporate", "Explosion", ["Explosion"]],
        [
          "Body Corporate",
          "Fire",
          [
            "Electrical Fault",
            "Explosion",
            "External source",
            "Fire General",
            "Mechanical Fault",
            "Poor Housekeeping / Lack of Care",
            "Source off Premises",
            "Spontaneous Combustion",
            "Subterranean"
          ]
        ],
        [
          "Body Corporate",
          "Geyser",
          ["Repair", "Replacement", "With Resultant Water Damage", "Replace"]
        ],
        ["Body Corporate", "Illness", ["Accidental", "Building / Premises"]],
        ["Body Corporate", "Injury", ["Accidental", "Building / Premises"]],
        [
          "Body Corporate",
          "Leakage",
          [
            "Chemicals / Oils / Fumes",
            "Water - from Fire Extinguishing Installations",
            "Water - from Pipes",
            "Water - Leakage Costs"
          ]
        ],
        ["Body Corporate", "Liability", ["General", "Security Firm"]],
        ["Body Corporate", "Locks & Keys", ["Locks & Keys"]],
        ["Body Corporate", "Malicious Damage", ["Malicious Damage", "Malicious Damage With Theft"]],
        ["Body Corporate", "Riot & Strike", ["NASRIA", "Outside RSA / NAM", "SASRIA"]],
        ["Body Corporate", "Storm", ["Hail / Snow", "Lightning", "Storm / Wind", "Water / Flood"]],
        ["Body Corporate", "Subsidence & Landslip", ["Extended", "Limited"]],
        [
          "Body Corporate",
          "Theft",
          ["From Premises", "From Unattended Vehicle", "General", "With Force", "Without Force"]
        ],
        //Buildings Combined
        [
          "Buildings Combined",
          "Accidental Damage",
          ["Power Surge", "Public Supply Connections", "Tree Removal Costs"]
        ],
        ["Buildings Combined", "Earthquake", ["Mining Operations", "Natural Phenomenon"]],
        ["Buildings Combined", "Fire", ["Source off Premises"]],
        ["Buildings Combined", "Locks & Keys", ["Locks & Keys"]],
        [
          "Buildings Combined",
          "Malicious Damage",
          ["Malicious Damage", "Malicious Damage With Theft"]
        ],
        ["Buildings Combined", "Riot & Strike", ["NASRIA", "Outside RSA / NAM", "SASRIA"]],
        [
          "Buildings Combined",
          "Storm",
          ["Hail / Snow", "Lightning", "Storm / Wind", "Water / Flood"]
        ],
        ["Buildings Combined", "Subsidence & Landslip", ["Extended", "Limited"]],
        [
          "Buildings Combined",
          "Theft",
          ["From Premises", "From Unattended Vehicle", "General", "With Force", "Without Force"]
        ],

        //Business All Risks
        ["Business All Risks", "Accidental Damage", ["Lost / Misplaced"]],
        ["Business All Risks", "Earthquake", ["Mining Operations", "Natural Phenomenon"]],
        ["Business All Risks", "Fire", ["Source off Premises"]],
        [
          "Business All Risks",
          "Malicious Damage",
          ["Malicious Damage", "Malicious Damage With Theft"]
        ],
        ["Business All Risks", "Riot & Strike", ["NASRIA", "Outside RSA / NAM", "SASRIA"]],
        [
          "Business All Risks",
          "Storm",
          ["Hail / Snow", "Lightning", "Storm / Wind", "Water / Flood"]
        ],
        ["Business All Risks", "Subsidence & Landslip", ["Extended", "Limited"]],
        [
          "Business All Risks",
          "Theft",
          [
            "General",
            "From Premises",
            "With Force",
            "Without Force",
            "From Unattended Vehicle",
            "Hijack",
            "Temporarily Removed from Building"
          ]
        ],
        //Business Interruption

        ["Business Interruption", "Earthquake", ["Mining Operations", "Natural Phenomenon"]],
        ["Business Interruption", "Fire", ["Source off Premises"]],
        [
          "Business Interruption",
          "Malicious Damage",
          ["Malicious Damage", "Malicious Damage With Theft"]
        ],
        ["Business Interruption", "Riot & Strike", ["NASRIA", "Outside RSA / NAM", "SASRIA"]],
        [
          "Business Interruption",
          "Storm",
          ["Hail / Snow", "Lightning", "Storm / Wind", "Water / Flood"]
        ],
        ["Business Interruption", "Subsidence & Landslip", ["Extended", "Limited"]],
        [
          "Business Interruption",
          "Theft",
          ["General", "From Premises", "With Force", "Without Force"]
        ],
        //Contract Works
        ["Contract Works", "Earthquake", ["Mining Operations", "Natural Phenomenon"]],

        ["Contract Works", "Fire", ["Source off Premises"]],
        ["Contract Works", "Malicious Damage", ["Malicious Damage", "Malicious Damage With Theft"]],
        ["Contract Works", "Riot & Strike", ["NASRIA", "Outside RSA / NAM", "SASRIA"]],
        ["Contract Works", "Storm", ["Hail / Snow", "Lightning", "Storm / Wind", "Water / Flood"]],
        ["Contract Works", "Subsidence & Landslip", ["Extended", "Limited"]],
        ["Contract Works", "Theft", ["General", "From Premises", "With Force", "Without Force"]],
        //Electronic Equipment
        ["Electronic Equipment", "Accidental Damage", ["Power Surge"]],
        ["Electronic Equipment", "Earthquake", ["Mining Operations", "Natural Phenomenon"]],
        ["Electronic Equipment", "Fire", ["Source off Premises"]],
        [
          "Electronic Equipment",
          "Malicious Damage",
          ["Malicious Damage", "Malicious Damage With Theft"]
        ],
        ["Electronic Equipment", "Riot & Strike", ["NASRIA", "Outside RSA / NAM", "SASRIA"]],
        [
          "Electronic Equipment",
          "Storm",
          ["Hail / Snow", "Lightning", "Storm / Wind", "Water / Flood"]
        ],
        ["Electronic Equipment", "Subsidence & Landslip", ["Extended", "Limited"]],
        [
          "Electronic Equipment",
          "Theft",
          [
            "General",
            "From Premises",
            "With Force",
            "Without Force",
            "From Unattended Vehicle",
            "Hijack",
            "Temporarily Removed from Building"
          ]
        ],
        //Employers Liability
        [
          "Employers Liability",
          "Death",
          [
            "Accidental",
            "Building / Premises",
            "Assault",
            "Machinery / Equipment",
            "Road Accident",
            "Toxic Processes",
            "Unhygienic Conditions"
          ]
        ],
        [
          "Employers Liability",
          "Illness",
          [
            "Accidental",
            "Building / Premises",
            "Assault",
            "Machinery / Equipment",
            "Road Accident",
            "Toxic Processes",
            "Unhygienic Conditions"
          ]
        ],
        [
          "Employers Liability",
          "Injury",
          [
            "Accidental",
            "Building / Premises",
            "Assault",
            "Machinery / Equipment",
            "Road Accident",
            "Toxic Processes",
            "Unhygienic Conditions"
          ]
        ],
        //Fidelity Guarantee
        ["Fidelity Guarantee", "Theft", ["Employee Fidelity"]],
        //Fire
        ["Fire", "Accidental Damage", ["Power Surge", "Public Supply Connections"]],
        ["Fire", "Earthquake", ["Mining Operations", "Natural Phenomenon"]],
        ["Fire", "Fire", ["Source off Premises"]],

        ["Fire", "Malicious Damage", ["Malicious Damage", "Malicious Damage With Theft"]],
        ["Fire", "Riot & Strike", ["NASRIA", "Outside RSA / NAM", "SASRIA"]],
        ["Fire", "Storm", ["Hail / Snow", "Lightning", "Storm / Wind", "Water / Flood"]],
        ["Fire", "Subsidence & Landslip", ["Extended", "Limited"]],

        //Glass

        ["Glass", "Malicious Damage", ["Malicious Damage", "Malicious Damage With Theft"]],
        ["Glass", "Riot & Strike", ["NASRIA", "Outside RSA / NAM", "SASRIA"]],
        //Goods In Transit
        ["Goods In Transit", "Earthquake", ["Mining Operations", "Natural Phenomenon"]],
        ["Goods In Transit", "Fire", ["Source off Premises"]],
        [
          "Goods In Transit",
          "Malicious Damage",
          ["Malicious Damage", "Malicious Damage With Theft"]
        ],
        ["Goods In Transit", "Riot & Strike", ["NASRIA", "Outside RSA / NAM", "SASRIA"]],
        [
          "Goods In Transit",
          "Storm",
          ["Hail / Snow", "Lightning", "Storm / Wind", "Water / Flood"]
        ],
        ["Goods In Transit", "Subsidence & Landslip", ["Extended", "Limited"]],
        [
          "Goods In Transit",
          "Theft",
          ["From Unattended Vehicle", "General", "Hijack", "With Force", "Without Force"]
        ],
        //Group Personal Accident
        [
          "Group Personal Accident",
          "Death",
          [
            "Accidental",
            "Building / Premises",
            "Assault",
            "Machinery / Equipment",
            "Passive War",
            "Road Accident",
            "Toxic Processes",
            "Unhygienic Conditions"
          ]
        ],
        [
          "Group Personal Accident",
          "Illness",
          [
            "Accidental",
            "Building / Premises",
            "Assault",
            "Machinery / Equipment",
            "Road Accident",
            "Passive War",
            "Toxic Processes",
            "Unhygienic Conditions"
          ]
        ],
        [
          "Group Personal Accident",
          "Injury",
          [
            "Accidental",
            "Building / Premises",
            "Assault",
            "Machinery / Equipment",
            "Passive War",
            "Road Accident",
            "Toxic Processes",
            "Unhygienic Conditions"
          ]
        ],
        ["Group Personal Accident", "Medical Expenses", ["Accidental", "Assault", "Road Accident"]],
        //Homeowners
        ["Homeowners", "Accidental Damage", ["Public Supply Connections", "Tree Removal Costs"]],
        ["Homeowners", "Earthquake", ["Mining Operations", "Natural Phenomenon"]],
        ["Homeowners", "Fire", ["Source off Premises"]],
        ["Homeowners", "Locks & Keys", ["Locks & Keys"]],
        ["Homeowners", "Malicious Damage", ["Malicious Damage", "Malicious Damage With Theft"]],
        ["Homeowners", "Riot & Strike", ["NASRIA", "Outside RSA / NAM", "SASRIA"]],
        ["Homeowners", "Storm", ["Hail / Snow", "Lightning", "Storm / Wind", "Water / Flood"]],
        ["Homeowners", "Subsidence & Landslip", ["Extended", "Limited"]],
        [
          "Homeowners",
          "Theft",
          [
            "From Premises",
            "From Unattended Vehicle",
            "General",
            "Hijack",
            "Temporarily Removed from Building",
            "With Force",
            "Without Force"
          ]
        ],
        //House Holders
        ["House Holders", "Accidental Damage", ["Power Surge"]],
        ["House Holders", "Earthquake", ["Mining Operations", "Natural Phenomenon"]],
        ["House Holders", "Fire", ["Source off Premises"]],
        ["House Holders", "Locks & Keys", ["Locks & Keys"]],
        ["House Holders", "Malicious Damage", ["Malicious Damage", "Malicious Damage With Theft"]],
        [
          "House Holders",
          "Machinery Breakdown",
          ["Electrical Breakdown", "Electronical Breakdown", "Mechanical Breakdown"]
        ],
        ["House Holders", "Medical Expenses", ["Accidental"]],
        ["House Holders", "Riot & Strike", ["NASRIA", "Outside RSA / NAM", "SASRIA"]],
        ["House Holders", "Storm", ["Hail / Snow", "Lightning", "Storm / Wind", "Water / Flood"]],
        ["House Holders", "Subsidence & Landslip", ["Extended", "Limited"]],
        [
          "House Holders",
          "Theft",
          [
            "From Unattended Vehicle",
            "From Premises",
            "General",
            "Temporarily Removed from Building",
            "With Force",
            "Without Force"
          ]
        ],

        //Machinery Breakdown
        [
          "Machinery Breakdown",
          "Accidental Damage",
          ["Accidental Damage General", "Deterioration of Stock", "Power Surge"]
        ],
        ["Machinery Breakdown", "Explosion", ["Explosion"]],
        ["Machinery Breakdown", "Fire", ["Electrical Fault", "Explosion"]],
        [
          "Machinery Breakdown",
          "Machinery Breakdown",
          [
            "Centrifugal forces",
            "Electrical Breakdown",
            "Electronical Breakdown",
            "Excessive Electrical Pressure",
            "Faulty Casting, Construction, Erection",
            "Faulty Design",
            "Faulty Material",
            "Faulty Workmanship, Lack of Skill, Carelessness",
            "Loosening of Parts",
            "Mechanical Breakdown",
            "Obstruction or entry of foreign bodies",
            "Short Circuit",
            "Shortage of Water in Boilers",
            "Vibration, mal-adjustment, mal-alignment"
          ]
        ],

        //Money
        ["Money", "Injury", ["Assault"]],
        ["Money", "Medical Expenses", ["Assault"]],
        ["Money", "Locks & Keys", ["Locks & Keys"]],
        [
          "Money",
          "Money Loss",
          [
            "At Insured Premises During Business Hours",
            "At Insured Premises Outside Business Hours",
            "Cheque Fraud",
            "In the Custody of a Collector / Roundsman",
            "In the Custody of a Petrol Attendant",
            "In Transit By Employee(s)",
            "In Transit By Security Firm",
            "Official on Business Trip",
            "Officials Residence"
          ]
        ],
        ["Money", "Riot & Strike", ["NASRIA", "Outside RSA / NAM", "SASRIA"]],
        ["Money", "Death", ["Assault"]],
        //Office Contents
        ["Office Contents", "Accidental Damage", ["Power Surge", "Documents"]],
        ["Office Contents", "Earthquake", ["Mining Operations", "Natural Phenomenon"]],
        ["Office Contents", "Fire", ["Source off Premises"]],
        ["Office Contents", "Locks & Keys", ["Locks & Keys"]],
        [
          "Office Contents",
          "Malicious Damage",
          ["Malicious Damage", "Malicious Damage With Theft"]
        ],
        ["Office Contents", "Riot & Strike", ["NASRIA", "Outside RSA / NAM", "SASRIA"]],
        ["Office Contents", "Storm", ["Hail / Snow", "Lightning", "Storm / Wind", "Water / Flood"]],
        ["Office Contents", "Subsidence & Landslip", ["Extended", "Limited"]],
        [
          "Office Contents",
          "Theft",
          [
            "From Unattended Vehicle",
            "From Premises",
            "General",
            "Temporarily Removed from Building",
            "With Force",
            "Without Force"
          ]
        ],

        //Personal Liability
        [
          "Personal Liability",
          "CUP/PLIP",
          [
            "General - Additional Risks",
            "General - Difference in Conditions",
            "General - Excess Layer",
            "Motor - Additional Risks",
            "Motor - Difference in Conditions",
            "Motor - Excess Layer",
            "Property Owners - Additional Risks",
            "Property Owners - Difference in Conditions",
            "Property Owners - Excess Layer",
            "Tenants - Additional Risks",
            "Tenants - Difference in Conditions",
            "Tenants - Excess Layer",
            "Watercraft - Additional Risks",
            "Watercraft - Difference in Conditions",
            "Watercraft - Excess Layer"
          ]
        ],
        ["Personal Liability", "Death", ["Accidental"]],
        ["Personal Liability", "Injury", ["Accidental"]],
        ["Personal Liability", "Illness", ["Accidental"]],
        [
          "Personal Liability",
          "Liability",
          [
            "Bowls Full-House",
            "First Aid",
            "General",
            "Golfers Hole-in-One",
            "Lawn Mowers & Gardening Tools",
            "Neighbourhood Watch",
            "Security Firm",
            "Tenants Liability"
          ]
        ],

        //Plant
        ["Plant", "Earthquake", ["Mining Operations", "Natural Phenomenon"]],
        ["Plant", "Fire", ["Source off Premises"]],
        ["Plant", "Malicious Damage", ["Malicious Damage", "Malicious Damage With Theft"]],
        ["Plant", "Riot & Strike", ["NASRIA", "Outside RSA / NAM", "SASRIA"]],
        ["Plant", "Storm", ["Hail / Snow", "Lightning", "Storm / Wind", "Water / Flood"]],
        [
          "Plant",
          "Theft",
          [
            "From Unattended Vehicle",
            "From Premises",
            "General",
            "Temporarily Removed from Building",
            "With Force",
            "Without Force"
          ]
        ],

        //Public Liability
        [
          "Public Liability",
          "CUP/PLIP",
          [
            "General - Additional Risks",
            "General - Difference in Conditions",
            "General - Excess Layer",
            "Motor - Additional Risks",
            "Motor - Difference in Conditions",
            "Motor - Excess Layer",
            "Property Owners - Additional Risks",
            "Property Owners - Difference in Conditions",
            "Property Owners - Excess Layer",
            "Tenants - Additional Risks",
            "Tenants - Difference in Conditions",
            "Tenants - Excess Layer",
            "Watercraft - Additional Risks",
            "Watercraft - Difference in Conditions",
            "Watercraft - Excess Layer"
          ]
        ],
        ["Public Liability", "Death", ["Accidental"]],
        ["Public Liability", "Injury", ["Accidental"]],
        ["Public Liability", "Illness", ["Accidental"]],
        [
          "Public Liability",
          "Liability",
          [
            "Animals - Auctions",
            "Animals - Driven / Escaped / Stray",
            "Animals - Grazing (not Insureds)",
            "Animals - Vehicles Drawn by",
            "Beauty Salon",
            "Breaking / Overflowing of Walls / Dams",
            "Car Parks",
            "Car Wash & Valet",
            "Chemist Prescription",
            "Crop Spraying",
            "Defective Workmanship",
            "Fire Spread - Other Than Sugar-Cane and Plantations",
            "Fire Spread - Sugar-Cane and Plantations",
            "First Aid",
            "Forecourt Service",
            "Game Hunting & Viewing",
            "General",
            "Government & Other Authorities Agreements",
            "Gratuitous Advice",
            "Hairdressers",
            "Hotel - Goods In Custody",
            "Lawn Mowers & Gardening Tools",
            "Neighbourhood Watch",
            "Products - Exported to E.E.C. Countries",
            "Products - Exported to Other Countries",
            "Products - Exported to U.S.A / Canada",
            "Products - General",
            "Products - Guarantee",
            "Products - Inefficacy",
            "Products - Recall",
            "Professional",
            "Schools",
            "Security Firm",
            "Statutory Legal Defence Costs",
            "Tenants Liability",
            "Tool of Trade",
            "Unattached Trailers",
            "Visitors & Employees Property",
            "Warehousemens Liability",
            "Work Away from Insured Premises",
            "Wrongful Act - Arrest & Defamation",
            "Wrongful Act - Directors",
            "Wrongful Act - Trustee"
          ]
        ],

        //Special Risks
        [
          "Special Risks",
          "Accidental Damage",
          [
            "Accidental Damage General",
            "Glass Buildings",
            "Impact by Aircraft / Aerial Devices",
            "Impact by Animals / Trees / Vehicles",
            "Power Surge",
            "Public Supply Connections",
            "Wild Animals Damage"
          ]
        ],
        ["Special Risks", "Claims Preparation Costs", ["Claims Preparation Costs"]],
        [
          "Special Risks",
          "CUP/PLIP",
          [
            "General - Additional Risks",
            "General - Difference in Conditions",
            "General - Excess Layer",
            "Motor - Additional Risks",
            "Motor - Difference in Conditions",
            "Motor - Excess Layer",
            "Property Owners - Additional Risks",
            "Property Owners - Difference in Conditions",
            "Property Owners - Excess Layer",
            "Tenants - Additional Risks",
            "Tenants - Difference in Conditions",
            "Tenants - Excess Layer",
            "Watercraft - Additional Risks",
            "Watercraft - Difference in Conditions",
            "Watercraft - Excess Layer"
          ]
        ],
        ["Special Risks", "Director & Officers Indemnity", ["Director & Officers Indemnity"]],
        ["Special Risks", "Earthquake", ["Mining Operations", "Natural Phenomenon"]],
        ["Special Risks", "Geyser", ["Repair", "Replace", "With Resultant Water Damage"]],
        ["Special Risks", "Explosion", ["Explosion"]],
        ["Special Risks", "Landscaped Gardens", ["Landscaped Gardens"]],
        [
          "Special Risks",
          "Leakage",
          [
            "Chemicals / Oils / Fumes",
            "Water - from Fire Extinguishing Installations",
            "Water - from Pipes",
            "Water - Leakage Costs"
          ]
        ],
        ["Special Risks", "Locks & Keys", ["Locks & Keys"]],
        ["Special Risks", "Malicious Damage", ["Malicious Damage", "Malicious Damage With Theft"]],
        ["Special Risks", "Riot & Strike", ["NASRIA", "Outside RSA / NAM", "SASRIA"]],
        ["Special Risks", "Storm", ["Hail / Snow", "Lightning", "Storm / Wind", "Water / Flood"]],
        ["Special Risks", "Subsidence & Landslip", ["Extended", "Limited"]],
        [
          "Special Risks",
          "Power Surge Cover to Non Electronic Equipment",
          ["Power Surge Cover to Non Electronic Equipment"]
        ],
        ["Special Risks", "Security Services", ["Security Services"]],
        [
          "Special Risks",
          "Theft",
          [
            "From Premises",
            "From Unattended Vehicle",
            "General",
            "Hijack",
            "Temporarily Removed from Building"
          ]
        ],
        [
          "Special Risks",
          "Vehicle - Accident",
          [
            "Animals / Trees",
            "Falling Objects",
            "Fixed Property",
            "Other vehicle(s)",
            "Parked - damaged by TP",
            "Pedestrian / Cyclist",
            "Potholes / Inappropriate road surface",
            "Single Vehicle Accident"
          ]
        ],
        ["Special Risks", "Vehicle - Glass", ["Repair", "Replace"]],
        [
          "Special Risks",
          "Vehicle - Roadside Assistance",
          [
            "Electrical Breakdown",
            "Flat Battery / Tyre",
            "Keys locked in vehicle",
            "Mechanical Breakdown"
          ]
        ],
        [
          "Special Risks",
          "Vehicle - Theft",
          [
            "Equipment & Damage to vehicle",
            "Equipment & No damage to vehicle",
            "Vehicle - Abscondence",
            "Vehicle - Hijack",
            "Vehicle - Theft"
          ]
        ],
        ["Special Risks", "Waiver of basic excess only", ["Waiver of basic excess only"]],
        ["Special Risks", "Wear and Tear on Internal Pipes", ["Wear and Tear on Internal Pipes"]],
        ["Special Risks", "Excess buy back", ["Excess buy back"]],
        [
          "Special Risks",
          "Fire",
          [
            "Electrical Fault",
            "Explosion",
            "External source",
            "Fire General",
            "Mechanical Fault",
            "Poor Housekeeping / Lack of Care",
            "Source off Premises",
            "Spontaneous Combustion",
            "Subterranean"
          ]
        ],
        //Stated Benefits
        [
          "Stated Benefits",
          "Death",
          [
            "Accidental",
            "Building / Premises",
            "Assault",
            "Machinery / Equipment",
            "Road Accident",
            "Toxic Processes",
            "Unhygienic Conditions",
            "Passive War"
          ]
        ],
        [
          "Stated Benefits",
          "Illness",
          [
            "Accidental",
            "Building / Premises",
            "Assault",
            "Machinery / Equipment",
            "Road Accident",
            "Toxic Processes",
            "Unhygienic Conditions",
            "Passive War"
          ]
        ],
        [
          "Stated Benefits",
          "Injury",
          [
            "Accidental",
            "Building / Premises",
            "Assault",
            "Machinery / Equipment",
            "Road Accident",
            "Toxic Processes",
            "Unhygienic Conditions"
          ]
        ],
        ["Stated Benefits", "Medical Expenses", ["Accidental", "Assault", "Road Accident"]],
        //Theft
        ["Theft", "Locks & Keys", ["Locks & Keys"]],
        ["Theft", "Theft", ["From Premises", "General", "With Force", "Without Force"]],
        //Watercraft
        ["Watercraft", "Earthquake", ["Natural Phenomenon"]],
        ["Watercraft", "Fire", ["Source off Premises"]],
        ["Watercraft", "Malicious Damage", ["Malicious Damage", "Malicious Damage With Theft"]],
        [
          "Watercraft",
          "Marine",
          [
            "Accidental Damage",
            "Capsizing",
            "Force of Nature",
            "Impact Damage",
            "Machinery Damage",
            "Malicious Damage",
            "Outboards Overboard",
            "Pirating",
            "Repairs Negligence",
            "Shortages / Non delivery of Cargo",
            "Sinking",
            "Stranding",
            "Submerged Object Struck",
            "War"
          ]
        ],
        ["Watercraft", "Medical Expenses", ["Accidental"]],
        ["Watercraft", "Riot & Strike", ["NASRIA", "Outside RSA / NAM", "SASRIA"]],
        ["Watercraft", "Storm", ["Hail / Snow", "Lightning", "Storm / Wind", "Water / Flood"]],
        [
          "Watercraft",
          "Theft",
          ["General", "From Premises", "Hijack", "With Force", "Without Force"]
        ]
      ],
      schemaIndex: 5,
      action: () => removeSchemasByTitle("Personal Injuries")
    }

    // Add more conditions as needed
  ];

  // Loop through NonmotorClaims to check conditions
  NonmotorClaims.forEach((type) => {
    if (claimNonMotor === type) {
      conditions.forEach((condition) => {
        if (
          checkCondition(
            claimNonMotor,
            claimNonMotorCauseOfLoss,
            NonMotorCausedBy,
            condition.causes
          )
        ) {
          condition.action(); // Execute action when condition is met
        }
      });
    }
  });

  // Remove "Loss Caused by Other Parties" unless third party is explicitly involved
  if (thirdparty !== "Yes") {
    const otherPartyIdx = schemas.findIndex((s) => s.title === "Loss Caused by Other Parties");
    if (otherPartyIdx !== -1) {
      schemas.splice(otherPartyIdx, 1);
      uiSchemas.splice(otherPartyIdx, 1);
    }
  }

  const Geyser = ({ selectFormData }) => {
    const fadeIn = useSpring({ opacity: 1, from: { opacity: 0 } });
  };
  RemoveQuestions();
  return (
    <animated.div style={fadeIn}>
      <h1>{schema.title}</h1>
      <h5>{schema.description}</h5>
      <Wizard
        schemas={schemas}
        uiSchemas={uiSchemas}
        selectFormData={selectFormData}
        onBack={onBack}
        readOnly={false}
      />
    </animated.div>
  );
};

export default Geyser;
